import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
  type RefObject,
} from "react";
import { Settings2, X } from "lucide-react";
import {
  Button,
  DescriptionItem,
  DescriptionList,
  Popover,
  PopoverContent,
  PopoverTrigger,
  PlayerAIRail,
  ProposalReviewDialog,
  getDefaultPlayerAIPrompts,
  Select,
  Switch,
  Tooltip,
  useRetainedValue,
  useProposalDeadlineClock,
  proposalExpired,
  type PlayerAIDraftRequest,
  type PlayerAIIntegrationService,
  type ProposalReviewState,
  type PlayerAIChatMessage,
  type PlayerAIProposalCanvasDestination,
  type PlayerAIProposalCanvasRevealOutcome,
  type PlayerAIContext,
  type PlayerAIFailureNotice,
  type PlayerAIMessageFeedback,
  type PlayerAIMessageFeedbackRequest,
  type PlayerAIMode,
  type PlayerAIApprovalNotice,
  type PlayerAIPauseNotice,
  activityWidgetLabel,
  announce,
  runActivityLabel,
  type PlayerAIRunActivityRow,
  type PlayerAIRunControl,
  type PlayerAITabId,
  type PlayerAIThread,
  type PlayerAIUnresolvedSources,
  type PlayerAIWidgetActionDetail,
  type SystemNotice,
  proposalApplyErrorCopy,
} from "@playbookai/ui";
import {
  MAX_MESSAGE_CHARACTERS,
  describePlayerAIFailure,
  hasSingletonPrivateConversation,
  playerAIFailureDetails,
  usePlayerAI,
  type Conversation,
  type ConversationProfileConfiguration,
  type ConversationSpecialistSettings,
  type EditableProposalForm,
  type GroupSchedule,
  type GroupScheduleState,
  type GroundingSourceRef,
  type InteractionContext,
  type Message,
  type PlayerAIScope,
  type PlatformActionBatch,
  type Proposal,
  type ProposalBatchDecisionItem,
  type ProposalDecisionSelection,
  type RunActivity,
  type RunProgress,
} from "@playbookai/player-ai-client";
import type {
  PlayerAIClientAction,
  PlayerAIWorkspacePresence,
} from "@playbookai/api-clients";
import {
  apiCall,
  useApiClient,
  type ApiClientConfig,
} from "@playbookai/api-clients";
import type {
  PlayerAICanvasDeliveryBinding,
  PlayerAICanvasDeliveryStatus,
} from "@playbookai/ui";
import type {
  PlatformAction,
  PlatformActionItemResult,
  PlatformModuleId,
  PlayerAISendResultDetail,
  PrivatePlayerAIEntityAttachment,
} from "@playbookai/types";
import { useAuthStore } from "../auth/auth-store.js";
import { canAdministerWorkspace } from "../auth/roles.js";
import { beginLogin } from "../auth/session-api.js";
import {
  isDeveloperBottomBarVisible,
  isGroupCollaborationEnabled,
} from "../config/runtime.js";
import { isModuleAvailable } from "../modules/index.js";
import {
  recordPermissionRefresh,
  type PermissionRefreshTrigger,
} from "./permission-refresh-log.js";

/**
 * How recently a permission check has to have completed for a window `focus`
 * to be treated as "nothing can have changed".
 *
 * Closing a native file picker raises `focus`, and so does dismissing an alert
 * — neither is a return from being away. Sixty seconds is long enough that a
 * picker round trip costs nothing and short enough that a real absence is
 * always re-checked; every other recovery path (reconnect, conversation
 * switch, the scoped access-change signal) is unaffected by it.
 */
export const PERMISSION_REFRESH_FRESHNESS_MS = 60_000;

/** Where an administrator adds the AI model Player AI answers with (d6). */
const GENAI_CONFIGURATION_ROUTE = "/admin/genai";

/**
 * The app a default "Try asking" prompt is about, by the prompt's id in
 * `getDefaultPlayerAIPrompts`. A prompt not listed here asks about no one app.
 */
const SUGGESTED_PROMPT_MODULE: Readonly<Record<string, PlatformModuleId>> = {
  // "Which KPIs are trending down?"
  h3: "kpi",
};
import { PersonalPanePreferences } from "./PersonalPanePreferences.js";
import { workspaceUnavailableNotice } from "./module-availability.js";
import { usePlayerAIReadiness } from "./usePlayerAIReadiness.js";
import { proposalApprovalImpact } from "./proposal-approval-impact.js";
import { ConversationModelProfileControls } from "./ConversationModelProfileControls.js";
import { usePlayerAIAttachments } from "./usePlayerAIAttachments.js";
import {
  sentWidgetAttachments,
  type WidgetAttachment,
} from "./widget-attachments.js";
import { isPlayerAIResponse, orderThread } from "./thread-order.js";
import {
  exchangeOfMessage,
  liveRunExchange,
  liveTriggerPreview,
  newMessageKeys,
  personMentionsOf,
  replyToOf,
  runExchanges,
  triggerPreviewOf,
  type Exchange,
} from "./rail-message-context.js";
import { useMessageNavigation } from "./useMessageNavigation.js";
import { useMyExchangesOnly } from "./useMyExchangesOnly.js";
import {
  usePersonMentionSearch,
  type MentionSearchResource,
} from "./usePersonMentionSearch.js";
import { useTypingIndicator } from "./useTypingIndicator.js";
import {
  isSendRequestScope,
  type PlayerAISendRequest,
} from "./usePlayerAISendRequest.js";
import {
  PLAYER_AI_LANGUAGE,
  PLAYER_AI_LANGUAGE_OPTIONS,
} from "./player-ai-language.js";

/** Client actions the shell or the registered product surface may execute. */
const CLIENT_ACTION_IDS: ReadonlySet<string> = new Set([
  "platform.navigate@1",
  "hoshin.workshop_ideas.add_to_private_canvas",
]);

/**
 * Translate a card's generic selection detail into the API's typed contract.
 *
 * The design system stays vocabulary-free — it echoes back the indices its own
 * payload gave it — so mapping those onto `section_index` is the host app's
 * job, exactly like every other domain decision in this file.
 */
function proposalDecisionSelection(
  detail: PlayerAIWidgetActionDetail | undefined,
): ProposalDecisionSelection | undefined {
  if (detail?.kind !== "hoshin_workshop_duplicates") return undefined;
  if (detail.selections.length === 0) return undefined;
  return {
    kind: "hoshin_workshop_duplicates",
    replacements: detail.selections.map((selection) => ({
      section_index: selection.index,
      text: selection.text,
    })),
  };
}

export interface PlayerAIRailWiredProps {
  scope: PlayerAIScope | null;
  workspacePresence?: PlayerAIWorkspacePresence | null | undefined;
  /**
   * PB-4923 AC6 — scope-matched lookups from the active module, so a skip
   * notice can say "[person] is editing [field]" instead of "Someone else is
   * editing text". Both answer null/undefined when the module has not published
   * for this scope, which is the honest fallback rather than a guess.
   */
  widgetLookups?: {
    readonly nameForActor: (userId: string) => string | null;
    readonly widgetParams: (widgetId: string) => unknown;
    readonly revealWidget: (
      widgetId: string,
    ) => PlayerAIProposalCanvasRevealOutcome;
    /** Task 6 — where the mention picker searches the workspace's people. */
    readonly peopleSearchResource?: () => MentionSearchResource | undefined;
  };
  context?: PlayerAIContext | undefined;
  variant?: "expanded" | "compact" | undefined;
  initialTab?: PlayerAITabId | undefined;
  mode?: PlayerAIMode | undefined;
  open?: boolean | undefined;
  onExited?: (() => void) | undefined;
  initialFocus?: "none" | "composer" | undefined;
  draftRequest?: PlayerAIDraftRequest | undefined;
  /**
   * A message a feature asked Player AI to post on its own (`playerai:open`
   * with `send`). AppLayout owns it so it survives closing and reopening
   * this panel; the panel posts it only after a successful
   * `claimSendRequest`, and answers through `reportSendResult`.
   */
  sendRequest?: PlayerAISendRequest | undefined;
  claimSendRequest?: ((id: string) => boolean) | undefined;
  reportSendResult?: ((result: PlayerAISendResultDetail) => void) | undefined;
  widgetAttachments?: readonly WidgetAttachment[] | undefined;
  onConsumeWidgetAttachments?: ((ids: readonly string[]) => void) | undefined;
  widgetAttachmentNotice?: string | undefined;
  draftEntityAttachment?: PrivatePlayerAIEntityAttachment | undefined;
  onRemoveDraftEntityAttachment?: (() => void) | undefined;
  interactionContext?: InteractionContext | undefined;
  captureInteractionContext?:
    | (() => InteractionContext | undefined)
    | undefined;
  onClientAction?: ((action: PlayerAIClientAction) => boolean) | undefined;
  clientActionLedger?: RefObject<Map<string, Set<string>>> | undefined;
  refreshRevision?: number | undefined;
  onSetMode?: ((mode: PlayerAIMode) => void) | undefined;
  onToggle?: (() => void) | undefined;
}

/** Wire chat and requester-owned approval drafts to the shared rail and dialogs. */
export function PlayerAIRailWired({
  scope,
  workspacePresence,
  widgetLookups,
  context,
  variant,
  initialTab,
  mode,
  open,
  onExited,
  initialFocus,
  draftRequest,
  sendRequest,
  claimSendRequest,
  reportSendResult,
  widgetAttachments = [],
  onConsumeWidgetAttachments,
  widgetAttachmentNotice,
  draftEntityAttachment,
  onRemoveDraftEntityAttachment,
  interactionContext,
  captureInteractionContext,
  onClientAction,
  clientActionLedger,
  refreshRevision,
  onSetMode,
  onToggle,
}: PlayerAIRailWiredProps): ReactElement {
  const apiConfig = useApiClient();
  const {
    connectionState,
    streamReconnectAttempt,
    failure,
    authorityStatus,
    initializationError,
    retryInitialize,
    conversations,
    activeConversationId,
    messages,
    proposals: receivedProposals,
    platformActionBatch,
    run,
    capabilities,
    conversationSurface,
    eventCursor,
    assistantDraft,
    runProgress,
    runActivities,
    runFailure,
    isRunActive,
    isConversationLoading,
    isSubmittingMessage,
    hasMoreMessages,
    isLoadingMessages,
    createConversation,
    selectConversation,
    refreshConversation,
    sendMessage,
    decideDocumentWait,
    startNext,
    collaborationMode,
    groupSettings,
    webSearchEnabled,
    canChangeWebSearch,
    setWebSearchEnabled,
    backgroundReviewFailure,
    contextNotice,
    retryContextCompaction,
    canvasStatusLabel,
    canvasStatus,
    canvasStatusUnavailable,
    retryCanvasStatus,
    backgroundCanvasSettings,
    loadBackgroundCanvasSettings,
    conversationProfile,
    setConversationProfile,
    setBackgroundCanvasProfile,
    updateGroupSettings,
    decideProposal,
    decideProposalBatch,
    editPlatformActionBatch,
    applyPlatformActionBatch,
    loadPlatformActionBatch,
    saveMessageFeedback,
    loadOlderMessages,
    loadMessagesThrough,
    loadMentionIndex,
    stopRun,
    resumePendingRequests,
    discardPendingRequests,
    typingParticipants,
    signalTyping,
  } = usePlayerAI(scope);
  const proposalNow = useProposalDeadlineClock(
    receivedProposals
      .filter((proposal) => proposal.status === "pending")
      .map((proposal) => proposal.expires_at),
    connectionState,
  );
  const proposals = useMemo(
    () =>
      receivedProposals.map((proposal) =>
        proposalExpired(proposal.status, proposal.expires_at, proposalNow)
          ? { ...proposal, status: "expired" as const }
          : proposal,
      ),
    [receivedProposals, proposalNow],
  );
  const isGroup =
    collaborationMode === "group" && isGroupCollaborationEnabled();
  const refreshRef = useRef(refreshConversation);
  refreshRef.current = refreshConversation;
  const permissionRefreshRef = useRef<
    (trigger: PermissionRefreshTrigger) => void
  >(() => undefined);
  /*
   * Task 7 — WHICH signals ask for a fresh authority read.
   *
   * Two broad triggers were removed here, and both were broad in the same way:
   * they fired on activity that says nothing about this caller's access.
   *
   *  - the tenant-wide audit bus (`subscribeEntityAuditChanged`), whose nudge
   *    is content-free by construction — it carries version, type, tenant and
   *    timestamp and nothing else — so EVERY audited write by ANYONE in the
   *    tenant re-read every open rail's permissions;
   *  - a subscription to any query-cache invalidation whose key merely
   *    MENTIONED this workspace id, which is every ordinary canvas refetch.
   *
   * What replaces them is the scoped signal the shell bridge already carries:
   * `refreshRevision`, raised by the workspace that owns the resource when its
   * OWN authorization facts move — membership, role, the facilitator overlay,
   * the canvas lock, group mode, lifecycle. Three recovery paths remain, and
   * between them they cover a signal that never arrives: conversation
   * load/switch (the hook's own hydrate), reconnect, and return-after-away.
   */
  const lastPermissionCheckRef = useRef(0);
  useEffect(() => {
    if (!activeConversationId) return;
    let disposed = false;
    let running = false;
    let queued = false;
    const refresh = async () => {
      queued = true;
      if (running) return;
      running = true;
      // Coalesce synchronous invalidations, then retain one trailing refresh:
      // a change arriving DURING a read is not dropped, it is served by the
      // trailing pass.
      await Promise.resolve();
      try {
        while (queued && !disposed) {
          queued = false;
          await refreshRef.current(true);
          lastPermissionCheckRef.current = Date.now();
        }
      } finally {
        running = false;
      }
    };
    const request = (trigger: PermissionRefreshTrigger) => {
      recordPermissionRefresh(trigger);
      void refresh();
    };
    permissionRefreshRef.current = request;
    /*
     * The return-after-away FRESHNESS RULE, stated so it is not re-invented.
     *
     * A `focus` event means very little on its own: closing a file picker, an
     * alert, or a devtools panel raises one, and the rail was never away. So a
     * check is requested only when the last completed one is older than
     * `PERMISSION_REFRESH_FRESHNESS_MS`; anything sooner is recorded as
     * suppressed and does nothing. Long enough that a picker round trip is
     * free, short enough that a real absence is always re-checked.
     */
    const onFocus = () => {
      const since = Date.now() - lastPermissionCheckRef.current;
      if (since < PERMISSION_REFRESH_FRESHNESS_MS) {
        recordPermissionRefresh("return_fresh_skipped");
        return;
      }
      request("return_after_away");
    };
    window.addEventListener("focus", onFocus);
    return () => {
      disposed = true;
      permissionRefreshRef.current = () => undefined;
      window.removeEventListener("focus", onFocus);
    };
  }, [activeConversationId]);
  const observedRefreshRevisionRef = useRef(refreshRevision);
  useEffect(() => {
    if (refreshRevision === observedRefreshRevisionRef.current) return;
    observedRefreshRevisionRef.current = refreshRevision;
    permissionRefreshRef.current("access_change");
  }, [refreshRevision]);
  const previousConnectionState = useRef(connectionState);
  useEffect(() => {
    if (
      connectionState === "open" &&
      previousConnectionState.current === "reconnecting"
    ) {
      // Every live access signal reaches a LIVE connection only, so a change
      // that landed while this client was dropped was never delivered. This is
      // the recovery path for it, and the one that covers the legacy collab
      // protocol, where an authorization change closes the socket instead of
      // raising a scoped event.
      permissionRefreshRef.current("reconnect");
    }
    previousConnectionState.current = connectionState;
  }, [connectionState]);
  const currentUserId = useAuthStore((state) => state.claims?.userId ?? null);
  const authSessionGeneration = useAuthStore(
    (state) => state.claims?.authSessionGeneration ?? null,
  );
  const currentTenantId = useAuthStore(
    (state) => state.claims?.tenantId ?? null,
  );
  const localClientActionLedgerRef = useRef<Map<string, Set<string>>>(
    new Map(),
  );
  const isWorkspace = scope?.conversationType === "workspace";
  const isSolveWorkspace = isWorkspace && scope.workspaceType === "solve";
  useEffect(() => {
    if (isSolveWorkspace && activeConversationId) {
      void loadBackgroundCanvasSettings();
    }
  }, [activeConversationId, isSolveWorkspace, loadBackgroundCanvasSettings]);
  // ADR-0100 d8: a chat whose app is off is history. The server already
  // answers it read-only; this is the reason the reader is told, in the d7
  // words every screen uses.
  const tenantEnabledModules = useAuthStore(
    (state) => state.claims?.enabledModules,
  );
  const claims = useAuthStore((state) => state.claims ?? null);
  const scopeUnavailableNotice = workspaceUnavailableNotice(
    scope && "workspaceType" in scope ? scope.workspaceType : null,
    tenantEnabledModules,
  );
  // d6: whether Player AI can answer here at all, read when the panel opens.
  const { readiness, retry: retryReadiness } = usePlayerAIReadiness(scope);
  // Admin is a read-only Player AI surface. The root-private Today
  // conversation is shared across every platform page, so a batch drafted on
  // Home must not render an editable or confirmable affordance — nor accept
  // one — while the user is on Admin.
  const isAdminSurface =
    interactionContext?.current_page_context?.surface === "admin";
  const usesPrivateConversationPermissions =
    scope?.conversationType === "private" && conversationSurface !== "group";
  const canPost = usesPrivateConversationPermissions
    ? capabilities?.can_start_run === true
    : capabilities?.can_post_message === true;
  const canMutateAttachments = usesPrivateConversationPermissions
    ? capabilities?.can_start_run === true
    : capabilities?.can_post_message === true ||
      capabilities?.can_start_run === true;
  const onAttachmentAccessRefused = useCallback(() => {
    // The fourth mutating surface, treated like the other three: a 403 from an
    // upload is the server telling this client its access moved, and the check
    // it triggers is the same coalesced one.
    permissionRefreshRef.current("access_change");
  }, []);
  const attachmentSlots = usePlayerAIAttachments({
    conversationId: activeConversationId,
    canMutate: canMutateAttachments,
    onAccessRefused: onAttachmentAccessRefused,
  });
  const sourceNames = attachmentSlots.displayNamesByAttachmentId;
  // ADR-0100: never suggest asking about an app this workspace cannot use.
  const suggestedPrompts = useMemo(
    () =>
      getDefaultPlayerAIPrompts(context?.label).filter((prompt) => {
        const module = SUGGESTED_PROMPT_MODULE[prompt.id];
        return (
          module === undefined ||
          isModuleAvailable(module, tenantEnabledModules)
        );
      }),
    [context?.label, tenantEnabledModules],
  );
  // Activity is transient chrome, so hold it back briefly: a Run that answers
  // in under half a second should not flash a timeline the user cannot read.
  const showRunActivity = useDelayedFlag(runActivities.length > 0, 450);
  /**
   * How far each regeneration got, keyed by batch (or lone proposal).
   *
   * A REF, not state: it exists to stop a second click and a retry doing the
   * first half twice, and nothing renders from it. Three values, and the
   * middle one is the point — `rejected` means the old batch is already
   * discarded, so a retry re-asks WITHOUT rejecting again.
   */
  const regenerationRef = useRef<Map<string, "pending" | "rejected" | "done">>(
    new Map(),
  );
  /**
   * Regenerations whose replacement request failed after the discard landed.
   *
   * State rather than a ref, because the card has to keep its one action: the
   * batch is safely rejected and can execute nothing, so what is left is a
   * visible way back rather than a dead end.
   */
  const [regenerationRetryKeys, setRegenerationRetryKeys] = useState<string[]>(
    [],
  );
  /**
   * The open review dialog and its LOCAL draft.
   *
   * Held here, in the host, for two reasons. The design system component must
   * stay data-blind, and — more importantly — an edit is deliberately not
   * persisted anywhere until approval: there is no draft endpoint, no
   * autosave, and nothing in browser storage. Closing without approving loses
   * the edit, which is why the dialog confirms before it does.
   */
  const [review, setReview] = useState<{
    proposalId: string;
    valuesByProposalId: Record<string, Record<string, unknown>>;
    includedProposalIds: string[];
    selectedItemId: string | null;
    search: string;
  } | null>(null);

  const proposalPermissions = useCallback(
    (proposal: Proposal) => {
      if (
        proposal.status === "expired" ||
        proposalExpired(proposal.status, proposal.expires_at, Date.now())
      )
        return { approve: false, reject: false };
      if (isAdminSurface || capabilities?.can_read !== true)
        return { approve: false, reject: false };
      if (
        INTEGRATION_PRECONDITION_KINDS.has(
          String(proposal.precondition["kind"]),
        )
      ) {
        // The integration decision routes authorize the initiating human,
        // independently of native product proposal permission. Private chats
        // deliberately report can_decide_proposal=false for native changes.
        const own =
          currentUserId !== null &&
          proposal.precondition["initiating_user_id"] === currentUserId;
        return { approve: own, reject: own };
      }
      const approve = isGroup
        ? capabilities.collaboration?.can_decide_group_proposal === true
        : capabilities.can_decide_proposal === true;
      const requesterMayWithdraw =
        !isGroup ||
        capabilities.collaboration?.decisions_restricted_to_driver === false;
      return {
        approve,
        reject:
          approve ||
          (requesterMayWithdraw &&
            capabilities.can_post_message === true &&
            proposal.run_id === run?.id &&
            run.requested_by_user_id === currentUserId),
      };
    },
    [isAdminSurface, capabilities, currentUserId, isGroup, run],
  );
  // One rule for "this viewer decides this card": every member still pending
  // and approvable by them. The review dialog and the approval reminder
  // (PB-5447) both ask it, so they cannot disagree about who must act.
  const viewerDecides = useCallback(
    (group: readonly Proposal[]) =>
      group.every(
        (proposal) =>
          proposal.status === "pending" &&
          proposalPermissions(proposal).approve,
      ),
    [proposalPermissions],
  );

  // Group mode replaces both browser triggers: no presence auto-start and no
  // `Next` (D4). A Run begins from a structured mention or the schedule, both
  // server-owned, so a stale client cannot start one by pressing anything.
  const canMentionPlayerAI =
    isGroup && capabilities?.collaboration?.can_mention_player_ai === true;
  // Task 7 — Reply is offered wherever this viewer may send a message; the
  // server validates the target and the routing rules on every send.
  const canReplyToPlayerAI = canPost && !isAdminSurface;

  /**
   * The exact request a failed Run was answering, resolved from the Run's own
   * `trigger_message_id`.
   *
   * Retry used to send the sentence "Retry the previous request.", which asks
   * the model to work out what to redo and drops the turn's attachments. It is
   * resolved from the AUTHORITATIVE binding rather than from "the last user
   * message": a shared conversation accumulates other participants' messages
   * after the failure, and the last one is then somebody else's.
   *
   * Five fences, and each refuses rather than guesses:
   *
   * - The binding must come from the failed Run — live from `run`, and after a
   *   reload from `runFailure`, where hydration projects it because a failed
   *   Run is not the ACTIVE Run and is reported nowhere else.
   * - The Run must have been `message`-triggered. That excludes a `next` Run
   *   (the SOLVE opening greeting, which was nobody's request) and EVERY group
   *   Run: `_create_group_run` records `trigger_message_id=None` for a mention
   *   and for the schedule alike, so a group Run names no request to re-ask.
   *   Group triggering is server-owned anyway (ADR-0044 D4) — a participant
   *   mentions again themselves, from the composer.
   * - The requester must be this viewer. Re-asking on somebody else's behalf
   *   would post their words under this account.
   * - The Message must be present in the transcript this viewer already holds,
   *   still be theirs, and still be within the composer's own ceiling.
   * - The ORIGINAL interaction context travels with it, not the current one.
   *   The stored context names the widget and surface the request was about; a
   *   reader who has since navigated or changed selection would otherwise
   *   re-ask the same words against a different target.
   *
   * Unresolvable means NO retry (see `failureNotice`) rather than a wrong one.
   */
  const retryRequest = useMemo<{
    readonly text: string;
    readonly attachmentIds: readonly string[];
    readonly interactionContext: InteractionContext | undefined;
  } | null>(() => {
    const binding =
      run?.id === runFailure?.run_id && run?.status === "failed"
        ? run
        : runFailure;
    if (
      !binding ||
      runFailure === null ||
      binding.trigger_type !== "message" ||
      !binding.trigger_message_id ||
      !currentUserId ||
      binding.requested_by_user_id !== currentUserId
    ) {
      return null;
    }
    const original = messages.find(
      (message) => message.id === binding.trigger_message_id,
    );
    if (
      original === undefined ||
      original.role !== "user" ||
      original.author_user_id !== currentUserId
    ) {
      return null;
    }
    const text =
      typeof original.content.text === "string"
        ? original.content.text.trim()
        : "";
    if (text.length === 0 || text.length > MAX_MESSAGE_CHARACTERS) return null;
    const storedContext = original.content.interaction_context;
    return {
      text,
      attachmentIds: (original.content.attachments ?? []).map(
        (attachment) => attachment.attachment_id,
      ),
      interactionContext:
        typeof storedContext === "object" && storedContext !== null
          ? (storedContext as InteractionContext)
          : undefined,
    };
  }, [currentUserId, messages, run, runFailure]);

  /**
   * May this viewer re-ask the failed Run's request, right now?
   *
   * ONE predicate, read by the button and by the action handler, because they
   * had drifted: the button checked capabilities and safety while the handler
   * checked only the Run id, so a card rendered before a permission change — or
   * before the server-authoritative safety answer landed — could still act
   * through a stale callback.
   *
   * `failure.action === "retry"` is the safety half and is already
   * server-gated: `describePlayerAIFailure` downgrades a Run failure to the
   * review copy unless the projection's `retry_safe` is explicitly true, so an
   * older server, a Run that left a Proposal in any state, and a live cross-app
   * batch all land here as "no retry" without this file re-deriving any of it.
   */
  /*
   * The capability half is the ENDPOINT's, not a plausible-looking pair.
   *
   * A retry is a `message`-triggered `POST /runs`, and `create_run` gates that
   * differently by conversation type: `can_post_message` is checked ONLY for a
   * workspace conversation, and `can_start_run` for both. A private
   * conversation has no separate post route — creating the Run IS how the
   * message is persisted — which is why `Capabilities.private_owner()` is
   * `can_post_message=False, can_start_run=True`.
   *
   * Requiring both therefore withheld Try again from every private
   * conversation, including Home, where the owner is the only person who can
   * ever use it. Asking for the capability the server actually enforces is the
   * fix; it neither widens access (the server still gates every call) nor
   * loosens any of the other fences.
   */
  const retryReachesCreateRun =
    !isGroup &&
    conversationSurface !== "group" &&
    capabilities?.can_start_run === true &&
    (isWorkspace ? capabilities.can_post_message === true : true);
  const canRetryRun =
    failure?.action === "retry" &&
    failure.diagnostics.stage === "response" &&
    runFailure !== null &&
    failure.diagnostics.runId === runFailure.run_id &&
    retryRequest !== null &&
    retryReachesCreateRun &&
    !isRunActive &&
    !isSubmittingMessage;
  /**
   * The handler is an event callback, so it reads the predicate through a ref
   * rather than a closed-over value: a press can arrive after capabilities have
   * moved but before the callback identity has.
   */
  const retryEligibilityRef = useRef<{
    readonly allowed: boolean;
    readonly runId: string | null;
    readonly request: typeof retryRequest;
  }>({ allowed: false, runId: null, request: null });
  retryEligibilityRef.current = {
    allowed: canRetryRun,
    runId: runFailure?.run_id ?? null,
    request: retryRequest,
  };

  /**
   * The failure card's model: the catalogue's copy, the ONE recovery this host
   * can actually perform, and the safe diagnostics behind the copy control.
   *
   * A recovery is offered only where there is a real destination. `edit_request`
   * and `none` deliberately render no button — the composer is already the way
   * to shorten a request, and "ask an administrator" is not something the rail
   * can do. A `retry` whose failure did NOT come from a Run is a failed read,
   * so its destination is the conversation re-read rather than a new Run.
   */
  const failureNotice = useMemo(() => {
    // Nullish, not `=== null`: a host or a test holding an older
    // `UsePlayerAIResult` has no `failure` key at all, and a strict comparison
    // would fall through and dereference `undefined`.
    if (!failure) return null;
    const recovery = ((): PlayerAIFailureNotice["action"] => {
      switch (failure.action) {
        case "reconnect":
          return {
            label: isConversationLoading ? "Reconnecting…" : "Retry connection",
            action: "initFailure:retry",
            busy: isConversationLoading,
          };
        case "retry":
          if (canRetryRun) {
            return {
              label: "Try again",
              action: `runFailure:retry:${runFailure.run_id}`,
            };
          }
          // A failed READ re-reads. A Run retry that did not qualify offers
          // nothing — except while a Run is in flight, where the button stays
          // visible but disabled so a double-press has nothing to hit and the
          // card does not flicker away and back (AC9).
          if (failure.diagnostics.stage === "request") {
            return { label: "Try again", action: "failure:refresh" };
          }
          return ((runFailure !== null &&
            failure.diagnostics.runId === runFailure.run_id &&
            retryRequest !== null &&
            (isRunActive || isSubmittingMessage)) as boolean)
            ? {
                label: "Try again",
                action: `runFailure:retry:${runFailure!.run_id}`,
                busy: true,
              }
            : undefined;
        case "refresh":
          return { label: "Refresh", action: "failure:refresh" };
        case "sign_in":
          return { label: "Sign in again", action: "failure:signIn" };
        default:
          return undefined;
      }
    })();
    return {
      title: failure.title,
      message: failure.message,
      details: playerAIFailureDetails(failure),
      ...(recovery ? { action: recovery } : {}),
    } satisfies PlayerAIFailureNotice;
  }, [
    canRetryRun,
    failure,
    isConversationLoading,
    isRunActive,
    isSubmittingMessage,
    retryRequest,
    runFailure,
  ]);

  /**
   * d6 — what the panel says on open when Player AI cannot answer here: no AI
   * model ("isn't set up yet"; an administrator gets the way to fix it), an
   * outage, or a session that must sign in again. The same card as a failure,
   * because the reader's next step is the same kind of thing.
   */
  const readinessNotice = useMemo<PlayerAIFailureNotice | null>(() => {
    if (!readiness || readiness.state === "ready") return null;
    const { failure } = readiness;
    const details = playerAIFailureDetails(failure);
    if (readiness.state === "not_set_up") {
      return canAdministerWorkspace(claims)
        ? {
            title: failure.title,
            message: "Add an AI model in GenAI configuration to turn it on.",
            details,
            action: {
              label: "Open GenAI configuration",
              action: "readiness:configure",
            },
          }
        : { title: failure.title, message: failure.message, details };
    }
    const action =
      failure.action === "retry"
        ? { label: "Try again", action: "readiness:retry" }
        : failure.action === "sign_in"
          ? { label: "Sign in again", action: "failure:signIn" }
          : undefined;
    return {
      title: failure.title,
      message: failure.message,
      details,
      ...(action ? { action } : {}),
    };
  }, [claims, readiness]);

  const railMessages = useMemo<PlayerAIChatMessage[]>(() => {
    const proposalsWithReplacement = new Set(
      proposals.flatMap((proposal) =>
        proposal.supersedes_proposal_id
          ? [proposal.supersedes_proposal_id]
          : [],
      ),
    );
    const messagesById = new Map(
      messages.map((message) => [message.id, message]),
    );
    const keys = newMessageKeys(messages);
    // Task 17 — rows are marked only in a group conversation, for a known
    // viewer; the rail folds them only while the viewer asked it to.
    const viewerId = isGroup ? currentUserId : null;
    const answeredRuns = viewerId ? runExchanges(messages, viewerId) : null;
    const liveExchange = viewerId
      ? liveRunExchange(run, messages, viewerId)
      : undefined;
    const exchangeOfRun = (
      runId: string | null | undefined,
    ): Exchange | undefined =>
      runId
        ? (answeredRuns?.get(runId) ??
          (runId === run?.id ? liveExchange : undefined))
        : undefined;
    // Task 5 — each Run's late updates sit above its own response.
    const mapped: PlayerAIChatMessage[] = orderThread(
      messages,
      proposalTimelineGroups(proposals),
    ).map((item) => {
      if (item.kind === "message") {
        const { message } = item;
        return toRailMessage(
          message,
          sourceNames,
          collaborationMode === "group",
          {
            messagesById,
            newMessageKey: keys.byMessageId.get(message.id),
            canReply: canReplyToPlayerAI,
            exchange: viewerId
              ? (exchangeOfMessage(message, viewerId) ??
                exchangeOfRun(message.run_id))
              : undefined,
          },
        );
      }
      const proposalGroup = item.group;
      const proposal = proposalGroup[0]!;
      const row = toRailProposal(
        proposal,
        proposalPermissions(proposal).approve,
        proposalPermissions(proposal).reject,
        proposalGroup.some((candidate) =>
          proposalsWithReplacement.has(candidate.id),
        ),
        proposalGroup,
        regenerationRetryKeys.includes(regenerationKey(proposal)),
        widgetLookups,
        apiConfig,
      );
      // A proposal belongs to its Run's exchange and is never counted.
      const exchange = exchangeOfRun(proposal.run_id);
      return exchange ? { ...row, exchange } : row;
    });
    if (platformActionBatch && !isAdminSurface) {
      mapped.push(toRailPlatformActionBatch(platformActionBatch));
    }
    if (run?.status === "waiting_documents" && run.document_wait) {
      const pendingNames = run.document_wait.pending_attachment_ids.map(
        (attachmentId) => sourceNames.get(attachmentId) ?? "the attached file",
      );
      const many = pendingNames.length > 1;
      const names = pendingNames.join(", ") || "The attached file";
      if (run.document_wait.decision === null) {
        mapped.push({
          id: `document-wait-${run.id}`,
          role: "assistant",
          content: `${names} ${many ? "are" : "is"} still processing. I can check ${many ? "them" : "it"} as soon as ${many ? "they’re" : "it’s"} ready, or continue now without ${many ? "them" : "it"}.`,
          widget: {
            kind: "quickReply",
            buttons: [
              {
                label: many ? "Wait for all files" : "Wait for file",
                action: `documentWait:wait:${run.id}`,
              },
              {
                label: many
                  ? "Continue with ready files"
                  : "Continue without it",
                action: `documentWait:continue:${run.id}`,
              },
            ],
          },
        });
      } else if (run.document_wait.decision === "wait") {
        mapped.push({
          id: `document-wait-${run.id}`,
          role: "assistant",
          content: `I’ll check ${names} as soon as ${many ? "they’re" : "it’s"} ready.`,
        });
      } else {
        mapped.push({
          id: `document-wait-${run.id}`,
          role: "assistant",
          content: "Continuing now with the files that are ready.",
        });
      }
    }
    const liveRunMark = exchangeOfRun(run?.id);
    if (showRunActivity && runActivities.length > 0) {
      // Immediately before the streamed draft: the work that is producing the
      // answer currently being written.
      mapped.push({
        id: `run-activity-${run?.id ?? "pending"}`,
        role: "assistant",
        widget: {
          kind: "runActivity",
          rows: runActivities.map((activity) =>
            toRailActivityRow(activity, sourceNames),
          ),
        },
        ...(liveRunMark ? { exchange: liveRunMark } : {}),
      });
    }
    if (assistantDraft) {
      const preview = liveTriggerPreview(run, messagesById);
      mapped.push({
        id: `run-draft-${run?.id ?? "pending"}`,
        role: "assistant",
        content: assistantDraft,
        // The stored response takes over this key, so it counts once.
        ...(run ? { newMessageKey: keys.draftKey(run.id) } : {}),
        ...(preview ? { triggerPreview: preview } : {}),
        ...(liveRunMark
          ? { exchange: liveRunMark, exchangeCountAs: "ai" as const }
          : {}),
      });
    }
    if (
      run?.outcome === "stopped" &&
      !groupSettings?.pause &&
      !messages.some(
        (message) =>
          (message.run_id === run.id && isPlayerAIResponse(message)) ||
          (run.ended_at !== null &&
            Date.parse(message.created_at) > Date.parse(run.ended_at)),
      )
    ) {
      // Task 14 — a Run stopped before it showed anything has no response to
      // mark "Stopped", so the thread says it until the conversation moves on.
      mapped.push({
        id: `run-stopped-${run.id}`,
        role: "system",
        content: "Player AI stopped.",
      });
    }
    if (mapped.length === 0 && !scope) {
      mapped.push({
        id: "player-ai-idle",
        role: "system",
        content:
          "Player AI is available from Home, SOLVE, and Hoshin workspaces.",
      });
    }
    if (contextNotice && activeConversationId) {
      mapped.push({
        id: `context-compaction-${activeConversationId}`,
        role: "system",
        ...(contextNotice.status === "failed"
          ? {
              failureNotice: {
                title: "Couldn’t compact context",
                message:
                  "Your conversation is unchanged. Try again to compact its context.",
                action: {
                  label: "Try again",
                  action: `contextCompaction:retry:${activeConversationId}`,
                },
                details: "Context compaction failed.",
              },
            }
          : { content: "Context compacted. You can continue here." }),
      });
    }
    if (failureNotice) {
      /*
       * PB-5276. One card for every failure path, replacing the raw `error`
       * string and the two hand-built quick replies.
       *
       * The id is keyed by the FAILED RUN rather than by the message text, so
       * event replay, hydration and the failure finaliser all reconcile onto
       * one card instead of producing a second one whenever the wording
       * changed (AC6). A failure with no Run — a setup or request failure —
       * has exactly one at a time and keys on its stage.
       *
       * `initializationError` is no longer consulted here: the catalogue
       * already decides that a setup failure recovers by reconnecting, and
       * that an expired session or a refused authorization does not.
       */
      mapped.push({
        id: `player-ai-failure-${failure?.diagnostics.runId ?? failure?.diagnostics.stage ?? "unknown"}`,
        role: "system",
        failureNotice,
      });
    }
    if (readinessNotice) {
      // d6: one card, keyed on its state so a re-read replaces rather than
      // stacks it.
      mapped.push({
        id: `player-ai-readiness-${readiness?.state ?? "unknown"}`,
        role: "system",
        failureNotice: readinessNotice,
      });
    }
    return mapped;
  }, [
    assistantDraft,
    contextNotice,
    readiness?.state,
    readinessNotice,
    activeConversationId,
    proposalPermissions,
    canReplyToPlayerAI,
    collaborationMode,
    failure,
    failureNotice,
    groupSettings?.pause,
    isAdminSurface,
    isGroup,
    messages,
    proposals,
    platformActionBatch,
    currentUserId,
    regenerationRetryKeys,
    run,
    runActivities,
    runFailure,
    scope,
    showRunActivity,
    sourceNames,
    apiConfig,
  ]);

  const railThreads = useMemo<PlayerAIThread[]>(
    () =>
      conversations.map((conversation) =>
        toRailThread(
          conversation,
          // d8: a thread of an off app is listed greyed, with the reason.
          workspaceUnavailableNotice(
            conversation.workspace_type,
            tenantEnabledModules,
          ),
        ),
      ),
    [conversations, tenantEnabledModules],
  );

  const autoStartWorkspaceRun =
    isWorkspace &&
    !isGroup &&
    workspacePresence?.status === "known" &&
    workspacePresence.presentUserCount === 1 &&
    capabilities?.can_post_message === true &&
    capabilities.can_start_run === true;

  const onWidgetAction = useCallback(
    (action: string, detail?: PlayerAIWidgetActionDetail) => {
      const [kind, value, id] = action.split(":");
      // d6: re-read whether Player AI can answer here; it sends nothing.
      if (kind === "readiness" && value === "retry") {
        retryReadiness();
        return;
      }
      // d6, administrators only: the one place an AI model is added.
      if (kind === "readiness" && value === "configure") {
        window.location.assign(GENAI_CONFIGURATION_ROUTE);
        return;
      }
      if (kind === "contextCompaction" && value === "retry") {
        if (id === activeConversationId && contextNotice?.status === "failed") {
          void retryContextCompaction();
        }
        return;
      }
      if (
        kind === "documentWait" &&
        id === run?.id &&
        (value === "wait" || value === "continue")
      ) {
        void decideDocumentWait(value);
        return;
      }
      // PB-1586: re-run session setup in place. Ignored while a retry is
      // already loading, so repeated presses cannot stack overlapping
      // initializations — and it sends nothing, so it can never duplicate a
      // message or start a Run.
      if (kind === "initFailure" && value === "retry") {
        if (!isConversationLoading) retryInitialize();
        return;
      }
      // PB-5276: a failed READ is recovered by re-reading, not by a new Run.
      // `refreshConversation` grants nothing and writes nothing, so a repeated
      // press is a repeated read.
      if (kind === "failure" && value === "refresh") {
        void refreshConversation();
        return;
      }
      // An expired session, sent to the one real destination this shell has.
      // `beginLogin` returns the URL to navigate to; a failure to even START
      // sign-in leaves the card exactly where it was rather than navigating
      // somewhere that cannot complete.
      if (kind === "failure" && value === "signIn") {
        void beginLogin(window.location.pathname + window.location.search)
          .then((url) => window.location.replace(url))
          .catch(() => undefined);
        return;
      }
      if (kind === "runFailure" && value === "retry") {
        /*
         * The SAME predicate the button is drawn from, re-read at press time.
         * A card can emit after capabilities changed, after a Run started, or
         * before the server's safety answer landed — and the id fence alone
         * caught none of those.
         */
        const eligibility = retryEligibilityRef.current;
        if (
          !eligibility.allowed ||
          eligibility.request === null ||
          // The id names the FAILED Run, so a card left over from an earlier
          // failure can never retry against a newer one.
          id !== eligibility.runId
        ) {
          return;
        }
        const {
          text,
          attachmentIds,
          interactionContext: original,
        } = eligibility.request;
        void (async () => {
          try {
            /*
             * `startRun: true`, NOT the presence-derived auto-start.
             *
             * `sendMessage` only reaches `createMessageRun` for a workspace
             * when this flag is set; with the auto-start flag it would post a
             * shared message and start no Run in any workspace holding more
             * than one participant — a Try again that visibly did nothing.
             * Presence is a heuristic for starting a Run the user did not ask
             * for; this is a Run they pressed a button for. A private
             * conversation takes `createMessageRun` regardless, so the flag is
             * simply uniform there, and the server still gates every call.
             *
             * No mention branch: a retry only ever exists for a
             * `message`-triggered Run, which group mode never produces.
             */
            await sendMessage(text, original, {
              startRun: true,
              ...(attachmentIds.length ? { attachmentIds } : {}),
            });
          } catch {
            // usePlayerAI already publishes the failure through `failure`.
          }
        })();
        return;
      }
      if (kind === "toolProposal" && id) {
        // Read-only surface: withholding the affordance is not enough, because
        // a card can still emit while the route changes under it.
        if (isAdminSurface) return;
        const openReview = (): void => {
          // Seeding the draft from the descriptor's own values is what makes
          // "dirty" mean "differs from what was proposed" rather than "the user
          // touched something".
          const target = proposals.find((candidate) => candidate.id === id);
          const batch = target
            ? reviewableProposalBatch(proposals, target)
            : [];
          const forms = batch
            .map((proposal) => [proposal.id, proposalForm(proposal)] as const)
            .filter(
              (entry): entry is readonly [string, EditableProposalForm] =>
                entry[1] !== null,
            );
          // A batch with an unrenderable member has NO review dialog, and the
          // card no longer offers one — `unsupportedReviewForm` replaces the
          // review, approve and reject controls with the single recovery
          // action. This branch therefore stops being the silent no-op it was:
          // it is unreachable from the card, and closing the dialog is the only
          // correct answer if it is ever reached another way.
          setReview(
            target === undefined || forms.length !== batch.length
              ? null
              : {
                  proposalId: id,
                  valuesByProposalId: Object.fromEntries(
                    forms.map(([proposalId, form]) => [
                      proposalId,
                      { ...form.values },
                    ]),
                  ),
                  includedProposalIds: batch.map((proposal) => proposal.id),
                  selectedItemId: id,
                  search: "",
                },
          );
        };
        if (value === "review") {
          openReview();
          return;
        }
        if (value === "approve" || value === "reject") {
          const target = proposals.find((candidate) => candidate.id === id);
          if (!target || !proposalPermissions(target)[value]) return;
          if (target !== undefined && target.approval_batch_id !== null) {
            // Approval batches are atomic. A stale card action must enter the
            // grouped review flow instead of calling the single-Proposal API,
            // which correctly refuses partial decisions.
            openReview();
            return;
          }
          void decideProposal(
            id,
            value,
            undefined,
            proposalDecisionSelection(detail),
          );
        }
        if (value === "regenerate") {
          // Regenerate = discard the OLD draft, then ask again through the same
          // structured-mention door every workshop request uses — the fresh
          // Run creates a fresh Proposal with current-version descriptors.
          //
          // Both halves can fail, and the SEQUENCING is the safety property. A
          // discard the server refused must NOT be followed by a re-ask (the
          // stale draft is still pinned, so the conversation would end up with
          // two live drafts), and neither failure may become an unhandled
          // rejection — this is `void`ed, so nothing downstream would catch
          // one. `usePlayerAI` resolves `false` and publishes through `error`
          // today; the try/catch is what stops that from being a load-bearing
          // assumption about a hook this file does not own.
          const target = proposals.find((candidate) => candidate.id === id);
          if (!target || !proposalPermissions(target).reject) return;
          const batch =
            target === undefined
              ? []
              : reviewableProposalBatch(proposals, target);
          // A card whose Proposal this view cannot see is decided by ID alone,
          // exactly as it always was. There is no batch to reason about, and
          // refusing to act would break the workshop's own Regenerate for a
          // conversation whose Proposals are not in this list. A native
          // product draft has no form to be missing, so it also takes the
          // ordinary path: the recovery below is for governed batches only.
          if (target === undefined || !isUnreviewableIntegrationBatch(batch)) {
            // THE ORDINARY REGENERATE, unchanged. A Hoshin workshop draft is
            // perfectly reviewable; the user simply wants a different one. It
            // is a single Proposal, it carries no machine-readable reason, and
            // its request is the workshop's own wording.
            //
            // Deliberately NOT folded into the recovery path below. That path
            // exists because a batch cannot be inspected at all, and giving an
            // ordinary discard the same bounded reason would put
            // "unsupported_review_form" on a decision record where it is
            // simply untrue.
            void (async () => {
              try {
                const discarded = await decideProposal(id, "reject");
                if (!discarded) return;
                await sendMessage(
                  "@Player AI Regenerate that analysis.",
                  undefined,
                  {
                    mentionsPlayerAI: true,
                    playerAIMentionOccurrenceIndices: [0],
                  },
                );
              } catch {
                // usePlayerAI already published this failure through `error`.
              }
            })();
            return;
          }
          const key = regenerationKey(target);
          const ledger = regenerationRef.current;
          // AT MOST ONE DECISION AND ONE REPLACEMENT PER BATCH.
          //
          // `pending` collapses a double-click; `done` collapses everything
          // after it, which is the case a click alone does not cover — the
          // first regeneration can finish between two presses, and without this
          // the second would reject an already-rejected batch and start a
          // second replacement Run. A batch that genuinely needs regenerating
          // again is a NEW batch with a new id, so it has its own entry.
          const progress = ledger.get(key);
          if (progress === "pending" || progress === "done") return;
          // `rejected` is the retry case: the discard already landed, so the
          // only half left to do is the re-ask.
          const alreadyRejected = progress === "rejected";
          ledger.set(key, "pending");
          void (async () => {
            if (!alreadyRejected) {
              // THE WHOLE BATCH, ATOMICALLY. An approval batch is one decision;
              // rejecting a single child through the per-Proposal API is
              // exactly what that API correctly refuses.
              let discarded = false;
              try {
                discarded =
                  target.approval_batch_id === null
                    ? await decideProposal(
                        target.id,
                        "reject",
                        UNSUPPORTED_REVIEW_FORM_REASON,
                      )
                    : await decideProposalBatch(
                        target.approval_batch_id,
                        batch.map((proposal) => ({
                          proposalId: proposal.id,
                          decision: "reject" as const,
                          reason: UNSUPPORTED_REVIEW_FORM_REASON,
                        })),
                      );
              } catch {
                // usePlayerAI already published this through `error`.
              }
              if (!discarded) {
                // NO RE-ASK. The old batch is still pending and still visible,
                // and asking for a replacement now would leave the conversation
                // holding two live drafts. Clearing the ledger is what makes
                // the button live again.
                ledger.delete(key);
                return;
              }
              // Recorded BEFORE the re-ask, so a re-ask that fails and is
              // retried does not reject an already-rejected batch a second time.
              ledger.set(key, "rejected");
            }
            try {
              await sendMessage(REGENERATE_SUGGESTIONS_MESSAGE, undefined, {
                mentionsPlayerAI: true,
                playerAIMentionOccurrenceIndices: [0],
              });
              ledger.set(key, "done");
              setRegenerationRetryKeys((current) =>
                current.includes(key)
                  ? current.filter((entry) => entry !== key)
                  : current,
              );
            } catch {
              // The batch is safely rejected; nothing can execute from it. What
              // the user needs is a way back, so the card keeps its one action
              // and pressing it re-asks WITHOUT rejecting again.
              ledger.set(key, "rejected");
              setRegenerationRetryKeys((current) =>
                current.includes(key) ? current : [...current, key],
              );
            }
          })();
        }
        return;
      }
      if (kind === "actionBatch" && id) {
        if (isAdminSurface) return;
        if (value === "apply" && id === platformActionBatch?.id) {
          void applyPlatformActionBatch();
          return;
        }
        if (value === "remainder") {
          void loadPlatformActionBatch(id);
          return;
        }
        if (
          id !== platformActionBatch?.id ||
          platformActionBatch.status !== "pending_confirmation"
        ) {
          return;
        }
        const actions = platformActionBatch.frozen.groups.flatMap((group) =>
          group.actions.map((item) => item.action),
        );
        if (value === "remove") {
          const removeAt = detail?.selections[0]?.index;
          if (
            typeof removeAt === "number" &&
            actions.length > 1 &&
            removeAt >= 0 &&
            removeAt < actions.length
          ) {
            void editPlatformActionBatch(
              actions.filter((_, index) => index !== removeAt),
            );
          }
          return;
        }
        // Bind the selections first: an optional-chained length comparison
        // reads as a guard but narrows nothing, so `detail` stayed possibly
        // undefined inside the block.
        const selections = detail?.selections;
        if (
          value === "edit" &&
          selections !== undefined &&
          selections.length === actions.length
        ) {
          try {
            const edited = actions.map((action, index) => ({
              ...action,
              input: JSON.parse(selections[index]?.text ?? "{}") as Record<
                string,
                unknown
              >,
            })) as PlatformAction[];
            void editPlatformActionBatch(edited);
          } catch {
            // The data-blind card validates JSON before emitting this action.
          }
        }
      }
    },
    [
      applyPlatformActionBatch,
      collaborationMode,
      autoStartWorkspaceRun,
      canMentionPlayerAI,
      decideDocumentWait,
      decideProposal,
      proposalPermissions,
      decideProposalBatch,
      editPlatformActionBatch,
      interactionContext,
      isAdminSurface,
      isConversationLoading,
      loadPlatformActionBatch,
      platformActionBatch,
      retryInitialize,
      retryContextCompaction,
      retryReadiness,
      contextNotice,
      activeConversationId,
      proposals,
      run?.id,
      runFailure?.run_id,
      sendMessage,
    ],
  );

  useEffect(() => {
    if (!onClientAction || !activeConversationId) return;
    const ledger =
      clientActionLedger?.current ?? localClientActionLedgerRef.current;
    let appliedMessageIds = ledger.get(activeConversationId);
    if (!appliedMessageIds) {
      appliedMessageIds = new Set();
      ledger.set(activeConversationId, appliedMessageIds);
    }
    for (const message of messages) {
      if (appliedMessageIds.has(message.id)) continue;
      const action = playerAiClientAction(message.content.client_action);
      if (
        !action ||
        !CLIENT_ACTION_IDS.has(action.action_id) ||
        // A TARGETED action names its recipient; every other session ignores
        // it (a UI convenience — the server re-authorizes any real effect).
        (typeof action["recipient_user_id"] === "string" &&
          action["recipient_user_id"] !== currentUserId)
      ) {
        continue;
      }
      if (onClientAction(action)) {
        appliedMessageIds.add(message.id);
      }
    }
  }, [
    activeConversationId,
    clientActionLedger,
    currentUserId,
    messages,
    onClientAction,
  ]);

  const onMessageFeedback = useCallback(
    async ({
      conversationId,
      messageId,
      rating,
      comment,
    }: PlayerAIMessageFeedbackRequest): Promise<PlayerAIMessageFeedback> => {
      const saved = await saveMessageFeedback(conversationId, messageId, {
        rating,
        comment,
      });
      return { rating: saved.rating, comment: saved.comment };
    },
    [saveMessageFeedback],
  );

  /*
   * Task 7 — `!canPost` moved OUT of `composerDisabled`.
   *
   * A disabled textarea cannot hold focus, so the browser moves the caret to
   * `<body>` the moment it is set and the next keystroke goes nowhere. That is
   * fine for "there is no conversation yet" and for "a Run is working", where
   * there is nothing to type into or the state is momentary and self-clearing.
   * It is wrong for an AUTHORITY state: somebody who has just lost access needs
   * to read and copy what they wrote, and someone in a read-only conversation
   * has a draft worth keeping. Those are read-only now, with the reason beside
   * the input.
   */
  const composerDisabled =
    isConversationLoading ||
    isSubmittingMessage ||
    !activeConversationId ||
    // D6: the group composer stays open while Player AI works — collaboration
    // must continue — and the storage coordinator handles the concurrency.
    (isRunActive && !isGroup) ||
    attachmentSlots.hasPendingUpload;
  const composerReadOnly = Boolean(activeConversationId) && !canPost;
  const composerPlaceholder = attachmentSlots.hasPendingUpload
    ? "Wait for the file upload to finish…"
    : // PB-1586: an initialization failure is NOT a read-only conversation.
      // Ranked above the `!canPost` branch below because `canPost` is derived
      // from capabilities the failed setup never fetched, so an outage fell
      // through to a sentence about AUTHORIZATION — telling the user they lack
      // access they in fact hold, and offering nothing to do about it. The
      // retry lives on the error message in the transcript, which is where the
      // reason is.
      initializationError
      ? isConversationLoading
        ? "Reconnecting to Player AI…"
        : "Player AI is unavailable — use Retry connection above."
      : isRunActive && !isGroup
        ? "Wait for the active Run to finish…"
        : authorityStatus === "unavailable"
          ? "Can't confirm your access right now — your draft is kept."
          : authorityStatus === "denied"
            ? "You no longer have access — your draft is kept so you can copy it."
            : // d8: the reason first, when the read-only answer is because
              // this chat's app is off.
              scopeUnavailableNotice && !canPost
              ? `${scopeUnavailableNotice}. This conversation is read-only.`
              : isWorkspace && !canPost
                ? "This conversation is read-only."
                : isGroup
                  ? canMentionPlayerAI
                    ? "Message the group and Player AI, or @mention someone…"
                    : "Add a message to the shared conversation…"
                  : isWorkspace
                    ? "Add a message to the shared conversation…"
                    : "Ask Player AI…";
  const showConversationSkeleton = useDelayedFlag(isConversationLoading, 300);
  const showReconnectStatus = useDelayedFlag(
    connectionState === "reconnecting",
    4_000,
  );
  const showSpecialistProgress = useDelayedFlag(runProgress !== null, 500);
  const composerStatus = attachmentSlots.hasPendingUpload ? (
    <span role="status" className="text-dense-1 text-pb-text-muted">
      Finish uploading the file before sending this message.
    </span>
  ) : /* Task 7 — the two AUTHORITY states that block, each with its reason and,
       where one exists, its recovery. Ranked above the connection notices: a
       reader who cannot send needs to know why before they are told the stream
       is catching up. A healthy check in flight says nothing at all. */
  authorityStatus === "unavailable" ? (
    /* The three notices below stay hand-built: Alert is a gap-3, px-3 py-2.5
       box with a glyph and pb-text copy, a look change no PR 3 row
       approves. */
    <div
      role="alert"
      className="flex items-center gap-2 rounded-md border border-pb-amber/40 bg-pb-amber-soft px-2 py-1 text-dense-1 text-pb-text-secondary"
    >
      <span>
        Couldn&apos;t confirm your access. Your draft is kept — try again.
      </span>
      <Button
        variant="secondary"
        size="sm"
        className="shrink-0"
        onClick={() => permissionRefreshRef.current("manual_retry")}
      >
        Try again
      </Button>
    </div>
  ) : authorityStatus === "denied" ? (
    <div
      role="alert"
      className="rounded-md border border-pb-red/40 bg-pb-red-soft px-2 py-1 text-dense-1 text-pb-text-secondary"
    >
      You no longer have access to this conversation. Your draft is kept here so
      you can copy it.
    </div>
  ) : connectionState === "reconnecting" && streamReconnectAttempt >= 5 ? (
    <div
      role="alert"
      className="rounded-md border border-pb-amber/40 bg-pb-amber-soft px-2 py-1 text-dense-1 text-pb-text-secondary"
    >
      Live updates are delayed. You can continue working; updates will catch up
      automatically.
    </div>
  ) : showReconnectStatus ? (
    <span role="status" className="text-dense-1 text-pb-text-muted">
      Reconnecting…
    </span>
  ) : null;
  const profileLabel =
    run?.ai_profile?.display_name ??
    conversationProfile?.settings?.effective_profile_name ??
    (isGroup ? "Workspace default" : "Conversation profile");

  // Who can see this conversation, said in the user's words. Two sources, in
  // order of authority:
  //
  // 1. `conversationSurface` — the SERVER's routing answer, for the scopes that
  //    resolve one (VSM). It is the only source that stays correct when the
  //    product moves the private/group boundary while the rail is open.
  // 2. the ACTIVE conversation's own metadata — Hoshin, whose two thread kinds
  //    are distinguishable from the row itself.
  //
  // Never inferred from the route or the phase: the pill is a privacy promise,
  // and a promise derived from a URL is one the server never made.
  const activeConversation =
    conversations.find(
      (conversation) => conversation.id === activeConversationId,
    ) ?? null;
  /*
   * A feature's send request (`playerai:open` with `send`), posted on its own.
   *
   * It waits for that workspace's chat to load — the first one that loads is
   * the target — and for Player AI to stop answering. Only then, right before
   * posting, is it claimed: a rebuilt panel, or StrictMode's second effect run,
   * gets `false` and posts nothing. It goes through the hook's `sendMessage`,
   * not the rail's own send, so nothing the person typed, pasted, attached,
   * replied to or mentioned goes with it. Leaving the workspace and the time
   * limit are AppLayout's to answer, because they apply with Player AI closed.
   */
  useEffect(() => {
    if (!sendRequest || !claimSendRequest || !reportSendResult) return;
    const { id: requestId, workspace } = sendRequest;
    if (
      !isSendRequestScope(scope, workspace) ||
      isConversationLoading ||
      capabilities === null ||
      activeConversation?.workspace_type !== workspace.type ||
      activeConversation.workspace_id !== workspace.id ||
      // The check failed rather than refused; it may recover.
      authorityStatus === "unavailable"
    ) {
      return;
    }
    if (!canPost) {
      reportSendResult({ requestId, ok: false, reason: "not_allowed" });
      return;
    }
    const sharedChat = isGroup || conversationSurface === "group";
    // A private chat takes nothing new while a card waits there, and a card
    // can wait 20 minutes, so say so now. A shared chat keeps taking messages.
    if (
      !sharedChat &&
      (run?.status === "waiting_approval" ||
        proposals.some((proposal) => proposal.status === "pending"))
    ) {
      reportSendResult({ requestId, ok: false, reason: "card_waiting" });
      return;
    }
    // A private chat takes nothing new while Player AI answers. A shared chat
    // keeps its composer open then (D6) and queues the request for the next
    // Run, so waiting there only risks the time limit on a long answer.
    if (isSubmittingMessage || (!sharedChat && isRunActive)) return;
    if (!claimSendRequest(requestId)) return;
    // Not cancelled on unmount: a claimed request reports its real outcome
    // even after Player AI closes, and AppLayout is still there to hear it.
    // `false` also means "sent, but the chat on screen changed meanwhile" (the
    // person left the VSM with Player AI open), so success is what the server
    // took, not what the screen still shows.
    let accepted = false;
    const report = (sent: boolean): void =>
      reportSendResult(
        sent
          ? { requestId, ok: true }
          : { requestId, ok: false, reason: "send_failed" },
      );
    sendMessage(
      sendRequest.text,
      captureInteractionContext?.() ?? interactionContext,
      {
        startRun: autoStartWorkspaceRun,
        onAccepted: () => {
          accepted = true;
        },
      },
    ).then(
      (applied) => report(applied || accepted),
      () => report(accepted),
    );
  }, [
    activeConversation,
    authorityStatus,
    autoStartWorkspaceRun,
    canPost,
    capabilities,
    captureInteractionContext,
    claimSendRequest,
    conversationSurface,
    interactionContext,
    isConversationLoading,
    isGroup,
    isRunActive,
    isSubmittingMessage,
    proposals,
    reportSendResult,
    run,
    scope,
    sendMessage,
    sendRequest,
  ]);
  // A singleton scope has exactly ONE durable private conversation per
  // participant, so there is no "new thread" to offer. Withholding the prop
  // rather than wiring a no-op: an affordance that does nothing reads as a bug,
  // and the whole point of the singleton is that the participant's preparation
  // context stays in one place.
  const canStartNewThread =
    scope?.conversationType === "private" &&
    !(
      "workspaceType" in scope &&
      hasSingletonPrivateConversation(scope.workspaceType)
    );
  const isVsmConversation = activeConversation?.workspace_type === "vsm";
  const chatAudience: "private" | "group" | null = isVsmConversation
    ? conversationSurface
    : activeConversation &&
        (activeConversation.workspace_type === "hoshin" ||
          activeConversation.workspace_type === "hoshin_workshop")
      ? activeConversation.conversation_type === "private"
        ? ("private" as const)
        : ("group" as const)
      : null;
  const chatAudienceLabel =
    chatAudience === null
      ? null
      : isVsmConversation
        ? chatAudience === "private"
          ? "Private · Only you"
          : "Group · VSM participants"
        : chatAudience === "private"
          ? "Private chat"
          : "Group chat";
  const chatAudienceTitle =
    chatAudience === "private"
      ? isVsmConversation
        ? "Only you can see this conversation, the files you attach here and anything you draft. It becomes shared only when you publish it."
        : "Visible only in your private Player AI conversation."
      : isVsmConversation
        ? "Visible to every participant in this VSM."
        : "Visible to workshop participants.";

  /*
   * Task 4 — `PLAYERAI_SHOW_DEVELOPER_BOTTOM_BAR` (deploy-time, default false).
   *
   * The bar's content is split rather than gated as a whole, because two of the
   * pills in it were never developer diagnostics: the chat-audience label is a
   * PRIVACY statement about where what you type will be visible, and the
   * pending-mention count is a request the room is waiting for you to answer.
   * Both survive the flag; raw Run status, the AI profile and the background
   * review badge do not.
   *
   * Read per render rather than at module scope so a test (and a page load
   * after a ConfigMap change) sees the current value rather than whatever was
   * in force when this module was first imported.
   */
  const showDeveloperBar = isDeveloperBottomBarVisible();
  const groupPause = isGroup ? (groupSettings?.pause ?? null) : null;
  // While Player AI is stopped, the shared pause status carries this count.
  const showPendingRequests =
    isGroup && !groupPause && (groupSettings?.pending_mention_count ?? 0) > 0;
  /* The footer chips stay hand-built: Badge's pill paints the elevated
     fill, pb-text and its own leading, a look change no PR 3 row approves.
     Their titles are plain-text hints on text that takes no focus, which
     keep the native title (d1). */
  const footerItems = (
    <>
      {showDeveloperBar && run ? (
        <span className="rounded-full border border-pb-border bg-pb-card px-2 py-0.5 text-dense-2 capitalize text-pb-text-secondary">
          {run.status.replace("_", " ")}
        </span>
      ) : null}
      {chatAudienceLabel ? (
        <span
          data-testid="player-ai-chat-audience"
          className="rounded-full border border-pb-border bg-pb-card px-2 py-0.5 text-dense-2 text-pb-text-secondary"
          title={chatAudienceTitle}
        >
          {chatAudienceLabel}
        </span>
      ) : null}
      {showPendingRequests ? (
        <span
          role="status"
          className="rounded-full border border-pb-border bg-pb-card px-2 py-0.5 text-dense-2 text-pb-text-secondary"
        >
          {groupSettings?.pending_mention_count === 1
            ? "1 more request waiting"
            : `${groupSettings?.pending_mention_count} more requests waiting`}
        </span>
      ) : null}
      {showDeveloperBar && !isGroup ? (
        <span
          className="max-w-36 truncate rounded-full border border-pb-border bg-pb-card px-2 py-0.5 text-dense-2 text-pb-text-muted"
          title={`AI profile: ${profileLabel}`}
        >
          {profileLabel}
        </span>
      ) : null}
      {showDeveloperBar && backgroundReviewFailure ? (
        <span
          role="status"
          className="rounded-full border border-pb-border bg-pb-card px-2 py-0.5 text-dense-2 text-pb-text-muted"
          title={
            backgroundReviewFailure.error_code
              ? `Background canvas review failed (${backgroundReviewFailure.error_code}).`
              : "Background canvas review failed."
          }
        >
          Background canvas review failed
        </span>
      ) : null}
      {showDeveloperBar && import.meta.env.DEV && run ? (
        /* A bare <details> can never animate its panel (ADR-0091) — and this
           one is an ANCHORED OVERLAY rather than a disclosure: it floats above
           the composer instead of taking height in the row, so the disclosure
           grade's 0fr → 1fr collapse would have nothing to measure. Popover is
           the chassis for that shape and already carries the overlay grade,
           the retained closing frame, and `inert` while it leaves. */
        <Popover align="start" contentRole="dialog">
          <PopoverTrigger>
            {/* eslint-disable-next-line ds/no-hand-built -- kept: a developer-only text trigger; Button link is cyan and plain adds a hover wash, a look change no PR 3 row approves. */}
            <button
              type="button"
              className="cursor-pointer text-dense-2 text-pb-text-muted"
            >
              Run details
            </button>
          </PopoverTrigger>
          {/* The run's label/value grid is a DescriptionList (OK-25). */}
          <PopoverContent padding="sm" className="w-72">
            <DescriptionList layout="grid" density="compact">
              <RunDetail label="Run" value={run.id} />
              <RunDetail
                label="Status"
                value={`${run.status}${run.outcome ? ` / ${run.outcome}` : ""}`}
              />
              <RunDetail label="Event" value={String(eventCursor)} />
              <RunDetail
                label="Execution ID"
                value={run.orchestration_execution_id ?? "Queued"}
              />
            </DescriptionList>
          </PopoverContent>
        </Popover>
      ) : null}
    </>
  );
  /*
   * `undefined`, never an empty element: the rail renders the footer's wrapper
   * only when a slot was supplied, so an empty fragment here would put an empty
   * flex child back under the composer — the reserved gap the flag exists to
   * remove. Every branch above is `showDeveloperBar && …` or one of the two
   * persistent pills, so this is exactly "nothing to show".
   */
  const hasFooterContent =
    (showDeveloperBar &&
      (Boolean(run) || !isGroup || Boolean(backgroundReviewFailure))) ||
    chatAudienceLabel !== null ||
    showPendingRequests;
  const footer = hasFooterContent ? (
    <div className="flex min-w-0 items-center gap-2">{footerItems}</div>
  ) : undefined;

  /* Task 14 — Stop while a Run works, and the shared pause after a stop. The
     server re-checks who may on every press; these only decide what is drawn. */
  const canControlRuns =
    capabilities?.can_control_runs === true && !isAdminSurface;
  const runControl: PlayerAIRunControl | undefined =
    isRunActive && run
      ? {
          state: run.stop_requested_at ? "stopping" : "running",
          canStop: canControlRuns,
          onStop: () => void stopRun(),
        }
      : undefined;
  const [pauseDecisionBusy, setPauseDecisionBusy] = useState(false);
  const decidePause = useCallback(
    async (decide: () => Promise<void>): Promise<void> => {
      setPauseDecisionBusy(true);
      try {
        await decide();
      } finally {
        setPauseDecisionBusy(false);
      }
    },
    [],
  );
  const pauseNotice: PlayerAIPauseNotice | undefined = groupPause
    ? {
        state: groupPause.state === "stopping" ? "stopping" : "stopped",
        pendingCount: groupPause.pending_request_count,
        canDecide: canControlRuns,
        busy: pauseDecisionBusy,
        onResume: () => void decidePause(resumePendingRequests),
        onDiscard: () => void decidePause(discardPendingRequests),
      }
    : undefined;

  /* Task 6 / Task 7 — mention navigation and reaching a referenced message,
     loading older history when it is not on screen. */
  const { mentionNavigation, focusRequest, requestMessage } =
    useMessageNavigation({
      conversationId: activeConversationId,
      currentUserId,
      mentionsEnabled: isGroup,
      messages,
      loadMentionIndex,
      loadMessagesThrough,
    });
  const [proposalFocus, setProposalFocus] = useState<{
    conversationId: string;
    id: string;
    messageId: string;
    /** PB-5447 Review: also move keyboard focus onto the card. */
    focus: boolean;
  } | null>(null);
  const [pendingProposalFocus, setPendingProposalFocus] = useState<{
    conversationId: string;
    proposalId: string;
    id: string;
    refreshed: boolean;
    /** When the refresh settled; a list that lands much later is unrelated. */
    refreshedAt: number | null;
    proposalsBeforeRefresh: readonly Proposal[];
    focus: boolean;
  } | null>(null);
  const proposalFocusSequence = useRef(0);
  useEffect(() => {
    setProposalFocus(null);
    setPendingProposalFocus(null);
  }, [focusRequest?.id, activeConversationId]);
  useEffect(() => {
    if (
      !pendingProposalFocus?.refreshed ||
      pendingProposalFocus.conversationId !== activeConversationId ||
      pendingProposalFocus.proposalsBeforeRefresh === proposals
    )
      return;
    // PB-5447 — a refresh that brought no new list (a blip, a superseded
    // read) must not leave a request that fires on some later, unrelated
    // update and pulls the reader — now with keyboard focus — away.
    if (Date.now() - (pendingProposalFocus.refreshedAt ?? 0) > 2_000) {
      setPendingProposalFocus(null);
      return;
    }
    const group = proposalTimelineGroups(proposals).find((items) =>
      items.some((proposal) => proposal.id === pendingProposalFocus.proposalId),
    );
    if (!group?.[0]) return;
    setProposalFocus({
      conversationId: pendingProposalFocus.conversationId,
      id: pendingProposalFocus.id,
      messageId: `proposal-${group[0].id}`,
      focus: pendingProposalFocus.focus,
    });
    setPendingProposalFocus(null);
  }, [pendingProposalFocus, activeConversationId, proposals]);
  const backgroundProposalState: Proposal["status"] | undefined = (
    {
      awaiting_approval: "pending",
      applying: "applying",
      awaiting_canvas_delivery: "applied",
      updated: "applied",
      failed: "failed",
      rejected: "rejected",
      expired: "expired",
      conflict: "stale",
    } as Partial<Record<string, Proposal["status"]>>
  )[
    canvasStatus?.failure_code === "CANVAS_DELIVERY_FAILED"
      ? "updated"
      : (canvasStatus?.stage ?? "")
  ];
  const statusRefreshKey = useRef<string | null>(null);
  useEffect(() => {
    const proposal = proposals.find(
      (item) => item.id === canvasStatus?.proposal_id,
    );
    // PB-5447 — status already awaits approval for a card the list has not
    // got yet: fetch it once, so the decider's reminder can appear. Not while
    // a conversation loads — the status may still be the previous one's, and
    // the load brings the list anyway.
    const awaitingUnseen =
      !isConversationLoading &&
      !proposal &&
      canvasStatus?.stage === "awaiting_approval" &&
      Boolean(canvasStatus.proposal_id);
    if (
      !awaitingUnseen &&
      (!proposal ||
        !backgroundProposalState ||
        proposal.status === backgroundProposalState)
    )
      return;
    const key = `${activeConversationId}:${canvasStatus?.proposal_id ?? ""}:${backgroundProposalState}:${canvasStatus?.updated_at ?? ""}`;
    if (statusRefreshKey.current === key) return;
    statusRefreshKey.current = key;
    void refreshRef.current();
  }, [
    activeConversationId,
    canvasStatus?.proposal_id,
    canvasStatus?.stage,
    canvasStatus?.updated_at,
    backgroundProposalState,
    isConversationLoading,
    proposals,
  ]);
  // The polled background status already disagrees with this card: it is on
  // its way out, so nothing should call attention to it.
  const contradictedByStatus = (proposal: Proposal) =>
    proposal.id === canvasStatus?.proposal_id &&
    backgroundProposalState !== undefined &&
    proposal.status !== backgroundProposalState;
  const attentionProposal = isSolveWorkspace
    ? [...proposals]
        .reverse()
        .find(
          (proposal) =>
            (proposal.status === "pending" || proposal.status === "failed") &&
            !contradictedByStatus(proposal),
        )
    : undefined;
  // PB-5447 — a pending approval is no longer the hint line's business: the
  // person who decides gets the reminder above the input, everyone else at
  // most plain status text. The hint still navigates to a failure.
  const hintProposalId =
    attentionProposal?.status === "failed"
      ? attentionProposal.id
      : canvasStatus?.stage === "failed"
        ? canvasStatus.proposal_id
        : null;
  // PB-5447 — the cards THIS viewer must decide, newest last in creation
  // order. Built only from durable state (status, deadline, server
  // capabilities), so a reload rebuilds the same reminder. A background card
  // stays `pending` on the server past its deadline, so the deadline is
  // checked here against the time now; the state only re-renders at the next
  // deadline when nothing else does.
  const [approvalClock, setApprovalClock] = useState(() => Date.now());
  const approvalNow = Math.max(approvalClock, Date.now());
  const approvalRows = isSolveWorkspace
    ? proposalTimelineGroups(proposals).filter(
        (group) =>
          viewerDecides(group) &&
          // A connected-service write this bundle cannot render for review is
          // a recovery state (Regenerate only), not an approval: it stays on
          // its card and is never advertised as one.
          !isUnreviewableIntegrationBatch(group) &&
          group.every(
            (proposal) =>
              Date.parse(proposal.expires_at) > approvalNow &&
              !contradictedByStatus(proposal),
          ),
      )
    : [];
  const nextApprovalDeadline =
    approvalRows.length > 0
      ? Math.min(
          ...approvalRows
            .flat()
            .map((proposal) => Date.parse(proposal.expires_at)),
        )
      : null;
  useEffect(() => {
    if (nextApprovalDeadline === null) return;
    const timer = window.setTimeout(
      () => setApprovalClock(Date.now()),
      // Past ~24.8 days setTimeout overflows and would fire at once.
      Math.min(
        Math.max(0, nextApprovalDeadline - Date.now() + 1),
        2_147_483_647,
      ),
    );
    return () => window.clearTimeout(timer);
  }, [nextApprovalDeadline]);
  const approvalTarget = approvalRows.reduce<Proposal[] | undefined>(
    (newest, group) =>
      newest === undefined ||
      Date.parse(group[0]!.created_at) >= Date.parse(newest[0]!.created_at)
        ? group
        : newest,
    undefined,
  );
  const foregroundCanvasActivity =
    isSolveWorkspace && isRunActive && showRunActivity
      ? [...runActivities]
          .reverse()
          .find(
            (activity) =>
              activity.status === "started" &&
              ([
                "reading_canvas_context",
                "checking_canvas_widgets",
                "inspecting_widgets",
                "checking_widget_actions",
                "reading_widget",
              ].includes(activity.kind) ||
                activity.kind.startsWith("preparing_widget_") ||
                activity.kind === "applying_widget_change"),
          )
      : undefined;
  const canvasNeedsAttention =
    canvasStatusUnavailable ||
    [
      "awaiting_approval",
      "failed",
      "conflict",
      "needs_input",
      "expired",
    ].includes(canvasStatus?.stage ?? "");
  // PB-5447 — approval speech belongs to the reminder's one announcement, so
  // the hint never repeats it: quiet while the reminder covers the approval
  // and while the status runs ahead of a card the list has not got yet. If
  // the status lookup fails meanwhile, the hint says only that, beside
  // "Retry status lookup" — never the stale approval line next to the reminder.
  // Covered means the reminder is for THAT card: a reminder for some other
  // approval leaves a card the viewer cannot decide its plain status text.
  const approvalCoveredByReminder =
    canvasStatus?.stage === "awaiting_approval" &&
    (approvalRows.some((group) =>
      group.some((proposal) => proposal.id === canvasStatus.proposal_id),
    ) ||
      !proposals.some((proposal) => proposal.id === canvasStatus.proposal_id));
  const bottomStatusLabel =
    attentionProposal?.status === "failed"
      ? "Update failed — review proposal"
      : approvalCoveredByReminder
        ? canvasStatusUnavailable
          ? "Canvas status unavailable"
          : null
        : foregroundCanvasActivity && !canvasNeedsAttention
          ? null
          : canvasStatusLabel;
  const canvasStatusStage = canvasStatus?.stage ?? canvasStatus?.state;
  const canvasCompletionLabel = hintProposalId
    ? null
    : canvasStatusStage === "updated"
      ? "Canvas Updated"
      : ["no_change", "needs_input", "conflict"].includes(
            canvasStatusStage ?? "",
          )
        ? "Canvas Unchanged"
        : null;
  const openStatusProposal = async (
    proposalId: string | null,
    focus = false,
  ) => {
    if (!proposalId || !activeConversationId) return;
    const group = proposalTimelineGroups(proposals).find((items) =>
      items.some((proposal) => proposal.id === proposalId),
    );
    const id = `canvas-status-${++proposalFocusSequence.current}`;
    // A card already in the list is revealed at once. Only a card the list
    // lacks, or one the polled status already contradicts, is fetched first —
    // otherwise Review would hang on an unrelated refresh.
    if (!group?.[0] || group.some(contradictedByStatus)) {
      setPendingProposalFocus({
        conversationId: activeConversationId,
        proposalId,
        id,
        refreshed: false,
        refreshedAt: null,
        proposalsBeforeRefresh: proposals,
        focus,
      });
      try {
        await refreshRef.current();
        setPendingProposalFocus((current) =>
          current?.id === id
            ? { ...current, refreshed: true, refreshedAt: Date.now() }
            : current,
        );
      } catch (error) {
        setPendingProposalFocus((current) =>
          current?.id === id ? null : current,
        );
        throw error;
      }
      return;
    }
    setPendingProposalFocus(null);
    setProposalFocus({
      conversationId: activeConversationId,
      id,
      messageId: `proposal-${group[0].id}`,
      focus,
    });
  };
  const approvalNotice: PlayerAIApprovalNotice | undefined =
    approvalTarget === undefined
      ? undefined
      : {
          ...approvalNoticeLabels(approvalTarget),
          onReview: () => void openStatusProposal(approvalTarget[0]!.id, true),
        };
  // Say it once per approval that newly needs this viewer, naming THAT
  // approval, through the shared live region — never again on a poll, a
  // re-render, a count going down, a mode switch or a reopen (see
  // `announcedApprovalIds`). Several new at once: the newest is said.
  const approvalSpeech = JSON.stringify(
    approvalRows.map((group) => [
      approvalAnnouncementKey(group),
      approvalNoticeLabels(group).label,
      Date.parse(group[0]!.created_at),
    ]),
  );
  useEffect(() => {
    let newest: { label: string; createdAt: number } | undefined;
    for (const [key, label, createdAt] of JSON.parse(approvalSpeech) as [
      string,
      string,
      number,
    ][]) {
      if (announcedApprovalIds.has(key)) continue;
      announcedApprovalIds.add(key);
      if (newest === undefined || createdAt >= newest.createdAt) {
        newest = { label, createdAt };
      }
    }
    if (newest !== undefined) announce(newest.label);
  }, [approvalSpeech]);
  /* Task 6 — load the complete authorized roster once, then filter in the picker. */
  const peopleSearchResource = widgetLookups?.peopleSearchResource?.();
  const personMentions = usePersonMentionSearch({
    resource: peopleSearchResource,
    viewerId: currentUserId,
    tenantId: currentTenantId,
    authSessionGeneration,
    enabled: isGroup && canPost && !isAdminSurface,
  });

  /* Task 15 — other participants typing; group conversations only. */
  const { typingNames, onComposerActivity } = useTypingIndicator({
    enabled: isGroup,
    participants: typingParticipants,
    currentUserId,
    conversationId: activeConversationId,
    signalTyping,
  });

  /* Task 17 — the personal "My exchanges only" display filter. */
  const [myExchangesOnly, setMyExchangesOnly] = useMyExchangesOnly({
    tenantId: currentTenantId,
    userId: currentUserId,
  });

  /* The dialog is hoisted onto `open` so it can animate out (ADR-0091), so
     everything its body reads must survive the frame after `review` is already
     null. Every derivation below therefore reads the RETAINED draft — which is
     the same object while the dialog is open, and the last one while it closes
     — and only `open` itself reads the live state. The draft is the one thing
     worth retaining: the proposal and its form are recomputed from it, so a
     proposal that leaves the list as the dialog closes simply unmounts it early
     rather than throwing on the way out. */
  const shownReview = useRetainedValue(review);
  const reviewAnchor =
    shownReview === null
      ? null
      : (proposals.find(
          (candidate) => candidate.id === shownReview.proposalId,
        ) ?? null);
  const reviewBatch = reviewAnchor
    ? reviewableProposalBatch(proposals, reviewAnchor)
    : [];
  const reviewProposal =
    shownReview === null
      ? null
      : (reviewBatch.find(
          (candidate) => candidate.id === shownReview.selectedItemId,
        ) ?? reviewAnchor);
  const reviewForm = reviewProposal ? proposalForm(reviewProposal) : null;
  const reviewValues =
    reviewProposal && shownReview
      ? (shownReview.valuesByProposalId[reviewProposal.id] ??
        reviewForm?.values ??
        {})
      : {};
  const includedProposalIds = new Set(shownReview?.includedProposalIds ?? []);
  const isBatchReview = reviewBatch.length > 1;
  const reviewDirty =
    shownReview !== null &&
    (shownReview.includedProposalIds.length !== reviewBatch.length ||
      reviewBatch.some((proposal) => {
        const form = proposalForm(proposal);
        return (
          form !== null &&
          JSON.stringify(
            shownReview.valuesByProposalId[proposal.id] ?? form.values,
          ) !== JSON.stringify(form.values)
        );
      }));
  // Only the initiating human may decide an integration Proposal — the server
  // enforces it, and mirroring it here stops the UI offering an approval that
  // would be refused. Everyone else in a shared conversation still sees it.
  const canDecideReview = reviewProposal !== null && viewerDecides(reviewBatch);
  const includedReviewProposals = reviewBatch.filter((proposal) =>
    includedProposalIds.has(proposal.id),
  );
  const approveReviewDisabled =
    includedReviewProposals.length === 0 ||
    includedReviewProposals.some((proposal) => {
      const form = proposalForm(proposal);
      return (
        form === null ||
        proposalFormHasErrors(
          form,
          shownReview?.valuesByProposalId[proposal.id] ?? form.values,
        )
      );
    });
  const approvalImpact = isBatchReview
    ? proposalApprovalImpact(
        includedReviewProposals.flatMap((proposal) => {
          const form = proposalForm(proposal);
          return form
            ? [
                {
                  id: proposal.id,
                  title: proposal.title,
                  service: reviewServiceLabel([proposal]),
                  target: proposalTargetLabel(proposal),
                  form,
                  values:
                    shownReview?.valuesByProposalId[proposal.id] ?? form.values,
                },
              ]
            : [];
        }),
        reviewBatch.length - includedReviewProposals.length,
      )
    : null;

  return (
    <>
      {shownReview && reviewProposal && reviewForm ? (
        <ProposalReviewDialog
          open={review !== null}
          onClose={() => setReview(null)}
          title={
            isBatchReview
              ? `Review ${reviewBatch.length} ${reviewServiceLabel(reviewBatch)} actions`
              : reviewProposal.title
          }
          description={
            isBatchReview
              ? "Review every proposed action together before Player AI continues."
              : reviewProposal.summary
          }
          service={proposalService(reviewProposal) ?? null}
          riskLabel="Write · Approval required"
          summary={{
            headline: isBatchReview
              ? `Run ${shownReview.includedProposalIds.length} of ${reviewBatch.length} proposed actions`
              : reviewProposal.title,
            detail: canDecideReview
              ? isBatchReview
                ? "Edit each action and choose which to approve. Unchecked actions will be rejected."
                : "Only you can edit and approve this suggestion."
              : "Only the requester can edit and approve these actions.",
          }}
          {...(isBatchReview
            ? {
                items: {
                  label: reviewItemsLabel(reviewBatch),
                  rows: reviewBatch
                    .filter((proposal) =>
                      proposalMatchesSearch(proposal, shownReview.search),
                    )
                    .map((proposal) => {
                      const form = proposalForm(proposal);
                      const values =
                        shownReview.valuesByProposalId[proposal.id] ??
                        form?.values ??
                        {};
                      const invalid =
                        form !== null && proposalFormHasErrors(form, values);
                      return {
                        id: proposal.id,
                        ...(proposalTargetLabel(proposal)
                          ? {
                              reference: proposalTargetLabel(
                                proposal,
                              ) as string,
                            }
                          : {}),
                        title: proposal.title,
                        included: includedProposalIds.has(proposal.id),
                        status: invalid ? "attention" : "valid",
                        ...(invalid
                          ? { statusHint: "Required fields need attention" }
                          : {}),
                      } as const;
                    }),
                  selectedId: reviewProposal.id,
                  onSelect: (proposalId: string) =>
                    setReview((current) =>
                      current === null
                        ? current
                        : { ...current, selectedItemId: proposalId },
                    ),
                  onToggle: (proposalId: string, included: boolean) =>
                    setReview((current) =>
                      current === null
                        ? current
                        : {
                            ...current,
                            includedProposalIds: included
                              ? [
                                  ...new Set([
                                    ...current.includedProposalIds,
                                    proposalId,
                                  ]),
                                ]
                              : current.includedProposalIds.filter(
                                  (candidate) => candidate !== proposalId,
                                ),
                          },
                    ),
                  searchValue: shownReview.search,
                  onSearchChange: (search: string) =>
                    setReview((current) =>
                      current === null ? current : { ...current, search },
                    ),
                  searchPlaceholder: `Search ${reviewItemsLabel(reviewBatch).toLowerCase()}`,
                  emptyLabel: "No matching actions.",
                },
              }
            : {})}
          form={{
            ...(isBatchReview
              ? {
                  heading:
                    proposalTargetLabel(reviewProposal) ?? reviewProposal.title,
                  ...(changedFieldCount(reviewForm, reviewValues) > 0
                    ? {
                        changedLabel: `${changedFieldCount(reviewForm, reviewValues)} changed ${changedFieldCount(reviewForm, reviewValues) === 1 ? "field" : "fields"}`,
                      }
                    : {}),
                }
              : {}),
            fields: reviewForm.fields.map((field) => ({
              path: field.path,
              label: field.label,
              control: field.control,
              required: reviewFieldRequired(field, reviewValues),
              editable: field.editable,
              ...(field.constraints ? { constraints: field.constraints } : {}),
              ...(field.options ? { options: field.options } : {}),
              ...(fieldError(field, reviewValues)
                ? {
                    error: fieldError(field, reviewValues) as string,
                  }
                : {}),
            })),
            values: reviewValues,
            onChange: (path, value) =>
              setReview((current) =>
                current === null
                  ? current
                  : {
                      ...current,
                      valuesByProposalId: {
                        ...current.valuesByProposalId,
                        [reviewProposal.id]: {
                          ...reviewForm.values,
                          ...current.valuesByProposalId[reviewProposal.id],
                          [path]: value,
                        },
                      },
                    },
              ),
          }}
          validationNote="Your edits will be validated before they are applied."
          dirty={reviewDirty}
          state={reviewProposalState(reviewProposal)}
          {...(reviewProposalState(reviewProposal) !== "editing"
            ? { stateMessage: reviewStateMessage(reviewProposal) }
            : {})}
          canDecide={canDecideReview}
          approveDisabled={approveReviewDisabled}
          {...(approvalImpact
            ? {
                approvalConfirmation: {
                  ...approvalImpact,
                  revisionKey: JSON.stringify({
                    conversationId: activeConversationId,
                    proposals: reviewBatch.map((proposal) => ({
                      id: proposal.id,
                      status: proposal.status,
                      updatedAt: proposal.updated_at,
                      form: proposalForm(proposal),
                      preview: proposal.preview,
                    })),
                    included: shownReview.includedProposalIds,
                    values: shownReview.valuesByProposalId,
                  }),
                },
              }
            : {})}
          onDiscard={() =>
            setReview((current) =>
              current === null
                ? current
                : {
                    ...current,
                    includedProposalIds: reviewBatch.map(
                      (proposal) => proposal.id,
                    ),
                    valuesByProposalId: Object.fromEntries(
                      reviewBatch.map((proposal) => [
                        proposal.id,
                        { ...(proposalForm(proposal)?.values ?? {}) },
                      ]),
                    ),
                  },
            )
          }
          onReject={() => {
            if (isBatchReview && reviewAnchor?.approval_batch_id) {
              void decideProposalBatch(
                reviewAnchor.approval_batch_id,
                reviewBatch.map((proposal) => ({
                  proposalId: proposal.id,
                  decision: "reject",
                })),
              );
            } else {
              void decideProposal(reviewProposal.id, "reject");
            }
            setReview(null);
          }}
          onApprove={() => {
            if (!canDecideReview || approveReviewDisabled) return;
            if (
              isBatchReview &&
              reviewAnchor?.approval_batch_id &&
              shownReview
            ) {
              const items: ProposalBatchDecisionItem[] = reviewBatch.map(
                (proposal) => {
                  const included = includedProposalIds.has(proposal.id);
                  if (!included) {
                    return {
                      proposalId: proposal.id,
                      decision: "reject",
                      reason: "Removed from this approval",
                    };
                  }
                  const form = proposalForm(proposal);
                  const values =
                    shownReview.valuesByProposalId[proposal.id] ??
                    form?.values ??
                    {};
                  const edited =
                    form !== null &&
                    JSON.stringify(values) !== JSON.stringify(form.values);
                  return {
                    proposalId: proposal.id,
                    decision: "approve",
                    ...(edited && form !== null
                      ? {
                          revision: {
                            version: 1 as const,
                            base_revision_hash: form.revisionHash,
                            values: editableValues(form, values),
                          },
                        }
                      : {}),
                  };
                },
              );
              void decideProposalBatch(reviewAnchor.approval_batch_id, items);
            } else {
              const edited =
                JSON.stringify(reviewValues) !==
                JSON.stringify(reviewForm.values);
              void decideProposal(
                reviewProposal.id,
                "approve",
                undefined,
                undefined,
                // Unchanged input takes the ORDINARY approval path. Sending a
                // revision that changes nothing would create a successor and
                // re-prepare the write for no reason.
                edited
                  ? {
                      version: 1,
                      base_revision_hash: reviewForm.revisionHash,
                      values: editableValues(reviewForm, reviewValues),
                    }
                  : undefined,
              );
            }
            setReview(null);
          }}
          {...(isBatchReview
            ? {
                approveLabel: `Approve selected (${includedReviewProposals.length})`,
              }
            : {})}
        />
      ) : null}
      <PlayerAIRail
        messages={railMessages}
        suggestedPrompts={suggestedPrompts}
        threads={railThreads}
        activeThreadId={activeConversationId}
        isThinking={
          isRunActive &&
          run?.status !== "waiting_approval" &&
          run?.status !== "waiting_documents" &&
          !assistantDraft &&
          // The structured timeline already says what is happening; a second
          // generic indicator beside it is noise.
          !(showRunActivity && runActivities.length > 0)
        }
        // The lifecycle behind that indicator. `isThinking` is suppressed for
        // every pause and for the streamed answer, so the rail cannot read
        // completion from it; this is the Run actually being over.
        isRunActive={isRunActive}
        onSendMessage={async (text, composerOptions) => {
          // Snapshot BEFORE the await: anything the user attaches while this send
          // is in flight belongs to the next message, and consuming by exact id
          // is what keeps it selected.
          const attachmentIds = attachmentSlots.pendingAttachments.map(
            (attachment) => attachment.id,
          );
          const isNextPhaseCommand = composerOptions?.command === "next_phase";
          if (isNextPhaseCommand && !isGroup) {
            await startNext();
            return;
          }
          const mentionsPlayerAI =
            canMentionPlayerAI && composerOptions?.mentionsPlayerAI === true;
          const widgetIds = widgetAttachments.map((item) => item.id);
          const capturedContext =
            captureInteractionContext?.() ?? interactionContext;
          // Explicit attachments, not whatever happens to be selected on canvas.
          const messageContext: InteractionContext | undefined =
            scope?.conversationType === "workspace" &&
            scope.workspaceType === "solve" &&
            (capturedContext || widgetAttachments.length > 0)
              ? {
                  ...capturedContext,
                  selection_refs: widgetAttachments.map(
                    (item) => item.reference,
                  ),
                }
              : capturedContext;
          const applied = await sendMessage(text, messageContext, {
            startRun: autoStartWorkspaceRun,
            ...(!isNextPhaseCommand && attachmentIds.length > 0
              ? { attachmentIds }
              : {}),
            ...(mentionsPlayerAI
              ? {
                  mentionsPlayerAI: true,
                  playerAIMentionOccurrenceIndices:
                    composerOptions?.playerAIMentionOccurrenceIndices ?? [],
                }
              : {}),
            // Task 6 — people are addressed only in a shared conversation.
            ...(isGroup && composerOptions?.personMentions?.length
              ? { personMentions: composerOptions.personMentions }
              : {}),
            // Task 7 — the Player AI response this message replies to.
            ...(!isNextPhaseCommand && composerOptions?.replyToMessageId
              ? { replyToMessageId: composerOptions.replyToMessageId }
              : {}),
            ...(isNextPhaseCommand ? { command: "next_phase" as const } : {}),
          });
          // Only on success — sendMessage rethrows, so a failed send leaves the
          // selection (and the rail's draft) exactly as the user left it.
          if (applied && !isNextPhaseCommand) {
            attachmentSlots.consumePendingAttachments(attachmentIds);
            onConsumeWidgetAttachments?.(widgetIds);
            onRemoveDraftEntityAttachment?.();
          }
        }}
        {...(canMentionPlayerAI
          ? {
              playerAIMention: {
                available: true,
                disabled: composerDisabled,
              },
            }
          : {})}
        {...((isGroup && isSolveWorkspace && canPost) ||
        (!isGroup && isSolveWorkspace && capabilities?.can_start_run === true)
          ? {
              // `/next_phase` is a SOLVE contract in both modes: a Hoshin
              // workshop's phase is advanced by its facilitator on the session
              // surface, and the server rejects the command elsewhere.
              nextPhaseCommand: {
                available: true,
                disabled: composerDisabled,
              },
            }
          : {})}
        groupConversation={isGroup}
        personMentions={personMentions}
        mentionNavigation={mentionNavigation}
        focusRequest={
          proposalFocus?.conversationId === activeConversationId
            ? proposalFocus
            : focusRequest
        }
        onRequestMessage={(messageId) => {
          setProposalFocus(null);
          requestMessage(messageId);
        }}
        runControl={runControl}
        pauseNotice={pauseNotice}
        approvalNotice={approvalNotice}
        typingNames={typingNames}
        onComposerActivity={onComposerActivity}
        myExchangesOnly={isGroup && myExchangesOnly}
        composerDisabled={composerDisabled}
        composerReadOnly={composerReadOnly}
        composerPlaceholder={composerPlaceholder}
        // The API contract this host owns. The rail refuses an over-long
        // message and names the number rather than trimming a pasted source.
        composerMaxLength={MAX_MESSAGE_CHARACTERS}
        showConversationSkeleton={showConversationSkeleton}
        composerStatusSlot={composerStatus}
        composerContextAttachments={widgetAttachments}
        onRemoveContextAttachment={(id) => onConsumeWidgetAttachments?.([id])}
        composerContextNotice={widgetAttachmentNotice}
        composerMetadataSlot={
          attachmentSlots.metadataSlot || draftEntityAttachment ? (
            <div className="space-y-2">
              {/* Kept hand-built: Chip is a Button-based pill with its own
                  paint and remove control, a look change no PR 3 row
                  approves. */}
              {draftEntityAttachment ? (
                <div className="flex min-w-0 items-center gap-2 rounded-md border border-pb-iris/30 bg-pb-iris-soft px-2 py-1.5">
                  <span className="min-w-0 flex-1 truncate text-xs text-pb-text-secondary">
                    {draftEntityAttachment.label}
                  </span>
                  {onRemoveDraftEntityAttachment ? (
                    // eslint-disable-next-line ds/no-hand-built -- kept: Button ghost iconOnly xs is this 24px box but with the 8px corner and the glyph in text-pb-text-secondary (today 4px and muted); no PR 3 look row approves that.
                    <button
                      type="button"
                      aria-label={`Remove ${draftEntityAttachment.label}`}
                      onClick={onRemoveDraftEntityAttachment}
                      className="flex h-6 w-6 shrink-0 items-center justify-center rounded text-pb-text-muted hover:pb-overlay-paint hover:text-pb-text active:pb-overlay-paint-strong focus-ring"
                    >
                      <X className="h-3.5 w-3.5" aria-hidden="true" />
                    </button>
                  ) : null}
                </div>
              ) : null}
              {attachmentSlots.metadataSlot}
            </div>
          ) : undefined
        }
        composerLeadingSlot={attachmentSlots.leadingSlot}
        composerFooterSlot={footer}
        composerHintSlot={
          bottomStatusLabel ? (
            <span role="status" aria-live="polite">
              {hintProposalId ? (
                <Button
                  variant="ghost"
                  size="xs"
                  onClick={() => void openStatusProposal(hintProposalId)}
                >
                  {bottomStatusLabel}
                </Button>
              ) : canvasCompletionLabel ? (
                <Tooltip
                  size="lg"
                  contentClassName="max-w-[min(24rem,calc(100vw-2rem))]"
                  content={
                    <>
                      {bottomStatusLabel}
                      {canvasStatusStage === "updated" &&
                      canvasStatus?.summary ? (
                        <p className="mt-2">{canvasStatus.summary}</p>
                      ) : null}
                    </>
                  }
                >
                  <Button variant="ghost" size="xs">
                    <span className="underline decoration-dotted underline-offset-4">
                      {canvasCompletionLabel}
                    </span>
                  </Button>
                </Tooltip>
              ) : (
                bottomStatusLabel
              )}
              {canvasStatusUnavailable && retryCanvasStatus ? (
                <Button variant="ghost" size="xs" onClick={retryCanvasStatus}>
                  Retry status lookup
                </Button>
              ) : null}
            </span>
          ) : foregroundCanvasActivity ? (
            <SpecialistProgress
              progress={{ started_at: foregroundCanvasActivity.started_at }}
              label={runActivityLabel(
                toRailActivityRow(foregroundCanvasActivity, sourceNames),
              )}
            />
          ) : showSpecialistProgress && runProgress ? (
            <SpecialistProgress progress={runProgress} />
          ) : undefined
        }
        headerActionsSlot={
          // Every group participant has at least "My exchanges only" (Task 17).
          <PlayerAISettingsControl
            isGroup={isGroup}
            vsmGroup={isGroup && isVsmConversation}
            {...(isGroup
              ? {
                  myExchangesOnly,
                  onMyExchangesOnlyChange: setMyExchangesOnly,
                }
              : {})}
            {...(conversationProfile?.settings &&
            Array.isArray(conversationProfile.profiles)
              ? {
                  conversationProfile,
                  backgroundCanvasSettings,
                  onConversationProfileChange: setConversationProfile,
                  onBackgroundProfileChange: setBackgroundCanvasProfile,
                }
              : {})}
            {...(webSearchEnabled != null &&
            modelCanSearch(conversationProfile) &&
            !(
              isGroup &&
              scope &&
              "workspaceType" in scope &&
              (scope.workspaceType === "hoshin_workshop" ||
                scope.workspaceType === "hoshin") &&
              !canChangeWebSearch
            )
              ? {
                  webSearchEnabled,
                  canChangeWebSearch,
                  onWebSearchChange: setWebSearchEnabled,
                }
              : {})}
            {...(isGroup && groupSettings?.can_configure
              ? {
                  groupSchedule: groupSettings.schedule,
                  onGroupScheduleChange: (schedule: GroupSchedule) =>
                    updateGroupSettings({ schedule }),
                }
              : {})}
          />
        }
        {...(canStartNewThread
          ? {
              onNewThread:
                widgetAttachments.length === 0
                  ? async () => {
                      await createConversation();
                    }
                  : async () => {
                      if (await createConversation()) {
                        onConsumeWidgetAttachments?.(
                          widgetAttachments.map((item) => item.id),
                        );
                      }
                    },
            }
          : {})}
        onSelectThread={async (thread) => {
          if (await selectConversation(thread.id)) {
            onConsumeWidgetAttachments?.(
              widgetAttachments.map((item) => item.id),
            );
          }
        }}
        onWidgetAction={onWidgetAction}
        onMessageFeedback={onMessageFeedback}
        onLoadOlderHistory={loadOlderMessages}
        hasMoreHistory={hasMoreMessages}
        isLoadingHistory={isLoadingMessages}
        {...(variant !== undefined ? { variant } : {})}
        {...(context !== undefined ? { context } : {})}
        {...(initialTab !== undefined ? { initialTab } : {})}
        {...(mode !== undefined ? { mode } : {})}
        {...(open !== undefined ? { open } : {})}
        {...(onExited !== undefined ? { onExited } : {})}
        {...(initialFocus !== undefined ? { initialFocus } : {})}
        {...(draftRequest !== undefined ? { draftRequest } : {})}
        {...(onSetMode !== undefined ? { onSetMode } : {})}
        {...(onToggle !== undefined ? { onToggle } : {})}
      />
    </>
  );
}

/** Map a durable Proposal status onto the dialog's own state vocabulary. */
function reviewProposalState(proposal: Proposal): ProposalReviewState {
  switch (proposal.status) {
    case "applying":
      return "applying";
    case "applied":
      return "applied";
    case "stale":
    case "expired":
      return "stale";
    case "failed":
    case "rejected":
      return "failed";
    default:
      return "editing";
  }
}

/**
 * Outcome copy for a non-editing state.
 *
 * Host-authored, and deliberately never `proposal.error` — that is a code and
 * whatever a provider said about it, neither of which is a sentence a user
 * should be shown.
 */
function reviewStateMessage(proposal: Proposal): string {
  switch (proposal.status) {
    case "applying":
      return "Applying your changes…";
    case "applied":
      return "Your changes were applied.";
    case "stale":
      return "This suggestion is out of date and was not applied. Ask Player AI to prepare it again.";
    case "expired":
      return "This suggestion expired before it was approved.";
    case "rejected":
      return "This suggestion was rejected.";
    default:
      return "This suggestion could not be applied.";
  }
}

/**
 * Immediate client-side validation, repeated by the server.
 *
 * Bounded to what the descriptor itself states, because that is all the browser
 * knows: the authoritative check runs against the CURRENT governed schema at
 * reprepare time, and this only exists so a user is not told about a problem
 * after a round trip.
 */
function reviewFieldRequired(
  field: EditableProposalForm["fields"][number],
  values: Record<string, unknown>,
): boolean {
  return (
    field.required &&
    (field.requiredUnless === undefined ||
      values[field.requiredUnless.field] !== field.requiredUnless.equals)
  );
}

function fieldError(
  field: EditableProposalForm["fields"][number],
  values: Record<string, unknown>,
): string | undefined {
  const value = values[field.path];
  if (!field.editable) return undefined;
  const empty =
    value === undefined ||
    value === null ||
    value === "" ||
    (Array.isArray(value) && value.length === 0);
  if (
    reviewFieldRequired(field, values) &&
    (empty ||
      (field.constraints?.nonBlank === true &&
        typeof value === "string" &&
        value.trim() === ""))
  )
    return `${field.label} is required.`;
  if (
    typeof value === "string" &&
    field.constraints?.maxLength !== undefined &&
    value.length > field.constraints.maxLength
  ) {
    return `${field.label} must be ${field.constraints.maxLength} characters or fewer.`;
  }
  if (typeof value === "number") {
    if (field.constraints?.min !== undefined && value < field.constraints.min) {
      return `${field.label} must be at least ${field.constraints.min}.`;
    }
    if (field.constraints?.max !== undefined && value > field.constraints.max) {
      return `${field.label} must be at most ${field.constraints.max}.`;
    }
  }
  return undefined;
}

/**
 * Narrow a draft to the fields the descriptor marked editable.
 *
 * The server refuses anything else outright, so this is not the gate — it
 * stops a fixed binding riding along in the payload at all, which keeps the
 * request honest about what it is asking to change.
 */
function editableValues(
  form: EditableProposalForm,
  values: Record<string, unknown>,
): Record<string, unknown> {
  return Object.fromEntries(
    form.fields
      .filter((field) => field.editable && field.path in values)
      .map((field) => [field.path, values[field.path]]),
  );
}

function SpecialistProgress({
  progress,
  label: suppliedLabel,
}: {
  progress: Pick<RunProgress, "started_at"> &
    Partial<Pick<RunProgress, "stage">>;
  label?: string;
}): ReactElement {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, []);

  const startedAt = Date.parse(progress.started_at);
  const elapsedSeconds = Number.isNaN(startedAt)
    ? 0
    : Math.max(0, Math.floor((now - startedAt) / 1_000));
  const minutes = Math.floor(elapsedSeconds / 60);
  const seconds = String(elapsedSeconds % 60).padStart(2, "0");
  const label =
    suppliedLabel ??
    (progress.stage === "reviewing_canvas"
      ? "Reviewing the canvas…"
      : "Preparing a canvas proposal…");

  return (
    <span role="status" aria-live="polite">
      {label} · {minutes}:{seconds}
    </span>
  );
}

/** AIS says which models can search; hide the setting for the ones that cannot. */
function modelCanSearch(
  configuration: ConversationProfileConfiguration | null | undefined,
): boolean {
  const effectiveId = configuration?.settings?.effective_profile_id;
  const profile = configuration?.profiles?.find((p) => p.id === effectiveId);
  return profile?.web_search_supported !== false;
}

function PlayerAISettingsControl({
  isGroup,
  vsmGroup,
  myExchangesOnly,
  onMyExchangesOnlyChange,
  webSearchEnabled,
  canChangeWebSearch,
  onWebSearchChange,
  groupSchedule,
  onGroupScheduleChange,
  conversationProfile,
  backgroundCanvasSettings,
  onConversationProfileChange,
  onBackgroundProfileChange,
}: {
  isGroup: boolean;
  vsmGroup: boolean;
  /** Task 17 — the personal display filter; group conversations only. */
  myExchangesOnly?: boolean | undefined;
  onMyExchangesOnlyChange?: ((enabled: boolean) => void) | undefined;
  webSearchEnabled?: boolean | undefined;
  canChangeWebSearch?: boolean | undefined;
  onWebSearchChange?: ((enabled: boolean) => Promise<void>) | undefined;
  groupSchedule?: GroupScheduleState | undefined;
  onGroupScheduleChange?:
    | ((schedule: GroupSchedule) => Promise<void>)
    | undefined;
  conversationProfile?: ConversationProfileConfiguration | undefined;
  backgroundCanvasSettings?: ConversationSpecialistSettings | null | undefined;
  onConversationProfileChange?:
    | ((profileId: string | null) => Promise<void>)
    | undefined;
  onBackgroundProfileChange?:
    | ((profileId: string | null) => Promise<void>)
    | undefined;
}): ReactElement {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const firstControlRef = useCallback(
    (node: HTMLInputElement | null) => {
      if (open) node?.focus();
    },
    [open],
  );
  const wasOpenRef = useRef(false);

  useEffect(() => {
    if (open) {
      wasOpenRef.current = true;
    } else if (wasOpenRef.current) {
      wasOpenRef.current = false;
      triggerRef.current?.focus();
    }
  }, [open]);

  const scheduleOptions = [
    { value: "off", label: "Off" },
    ...Array.from({ length: 15 }, (_, index) => {
      const minutes = index + 1;
      return {
        value: String(minutes),
        label: minutes === 1 ? "Every minute" : `Every ${minutes} minutes`,
      };
    }),
  ];

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      align="end"
      contentRole="dialog"
    >
      {/* The design-system tooltip in place of the native title (d1); the
          name already says it, so it does not describe the button again. */}
      <Tooltip content="Player AI settings" describeChild={false}>
        <PopoverTrigger>
          {/* eslint-disable-next-line ds/no-hand-built -- kept: Button ghost iconOnly sm is this 28px box but with the 8px corner (today 4px); no PR 3 look row approves that. */}
          <button
            ref={triggerRef}
            type="button"
            aria-label="Player AI settings"
            className="flex h-7 w-7 items-center justify-center rounded text-pb-text-secondary transition-colors hover:pb-overlay-paint hover:text-pb-text active:pb-overlay-paint-strong focus-ring"
          >
            <Settings2 className="h-3.5 w-3.5" />
          </button>
        </PopoverTrigger>
      </Tooltip>
      {/* The md padding step is today's p-3. The corner, iris border and card
          fill stay as they are: neither surface paints them, and no PR 3 look
          row approves the change. */}
      <PopoverContent
        aria-label="Player AI settings"
        padding="md"
        className="w-72 rounded-lg border-pb-iris/30 bg-pb-card"
      >
        <div className="mb-3">
          <div className="text-sm font-medium text-pb-text">
            Player AI settings
          </div>
          <div className="mt-0.5 text-dense-2 text-pb-text-muted">
            Preferences are shared with Player AI settings.
          </div>
        </div>
        <PersonalPanePreferences
          group={isGroup}
          vsmGroup={vsmGroup}
          firstControlRef={firstControlRef}
        />
        {/* Task 2 — the response language, where Player AI's own settings
            open: the same single, fixed choice as Settings → Player AI. */}
        <div className="mt-3 border-t border-pb-border pt-3">
          <Select
            id="player-ai-rail-language"
            label="Player AI language"
            value={PLAYER_AI_LANGUAGE}
            options={PLAYER_AI_LANGUAGE_OPTIONS}
          />
          <p className="mt-1.5 text-dense-2 text-pb-text-muted">
            Player AI always responds in English.
          </p>
        </div>
        {/* Kept hand-built like the Web search row below: SettingRow compact
            is a centred row with a divider under it. */}
        {onMyExchangesOnlyChange ? (
          <div className="mt-3 border-t border-pb-border pt-3">
            <div className="flex items-start justify-between gap-3">
              <span>
                <span className="block text-xs font-medium text-pb-text">
                  My exchanges only
                </span>
                <span className="mt-0.5 block text-dense-2 text-pb-text-muted">
                  Show your messages and Player AI’s responses to them. Everyone
                  else’s discussion folds into summaries. Only your view
                  changes.
                </span>
              </span>
              <Switch
                checked={myExchangesOnly === true}
                onChange={onMyExchangesOnlyChange}
                aria-label="My exchanges only"
              />
            </div>
          </div>
        ) : null}
        {conversationProfile &&
        onConversationProfileChange &&
        onBackgroundProfileChange ? (
          <ConversationModelProfileControls
            current={conversationProfile}
            background={backgroundCanvasSettings ?? null}
            onCurrentChange={onConversationProfileChange}
            onBackgroundChange={onBackgroundProfileChange}
          />
        ) : null}
        {/* Kept hand-built: SettingRow compact is a centred row with a
            divider under it, a look change no PR 3 row approves. */}
        {webSearchEnabled !== undefined && onWebSearchChange ? (
          <div className="mt-3 border-t border-pb-border pt-3">
            <div className="flex items-start justify-between gap-3">
              <span>
                <span className="block text-xs font-medium text-pb-text">
                  Web search
                </span>
                <span className="mt-0.5 block text-dense-2 text-pb-text-muted">
                  Allow Player AI to search the web from the next Run.
                </span>
              </span>
              <Switch
                checked={webSearchEnabled}
                disabled={!canChangeWebSearch}
                onChange={(enabled) => void onWebSearchChange(enabled)}
                aria-label="Web search"
              />
            </div>
            {!canChangeWebSearch ? (
              <p className="mt-1.5 text-dense-2 text-pb-text-muted">
                Set by the Owner or Facilitator.
              </p>
            ) : null}
          </div>
        ) : null}
        {groupSchedule && onGroupScheduleChange ? (
          <div className="mt-3 border-t border-pb-border pt-3">
            <Select
              id="player-ai-group-review-schedule"
              label="Review new discussion"
              value={
                groupSchedule.enabled
                  ? String(groupSchedule.interval_minutes)
                  : "off"
              }
              options={scheduleOptions}
              onChange={(value) => {
                const schedule: GroupSchedule =
                  value === "off"
                    ? {
                        enabled: false,
                        interval_minutes: groupSchedule.interval_minutes,
                      }
                    : { enabled: true, interval_minutes: Number(value) };
                void onGroupScheduleChange(schedule);
              }}
            />
            <p role="status" className="mt-1.5 text-dense-2 text-pb-text-muted">
              {groupReviewScheduleSummary(groupSchedule)}
            </p>
          </div>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}

function groupReviewScheduleSummary(schedule: GroupScheduleState): string {
  if (!schedule.enabled) return "Automatic review is off.";
  if (!schedule.effective) return "Automatic review is currently paused.";
  if (!schedule.next_check_at)
    return "The next review will be scheduled shortly.";
  const next = new Date(schedule.next_check_at);
  if (Number.isNaN(next.getTime())) return "The next review is scheduled.";
  return `Next review at ${next.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  })}.`;
}

function useDelayedFlag(active: boolean, delayMs: number): boolean {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!active) {
      setVisible(false);
      return;
    }
    const timer = window.setTimeout(() => setVisible(true), delayMs);
    return () => window.clearTimeout(timer);
  }, [active, delayMs]);

  return visible;
}

function RunDetail({
  label,
  value,
}: {
  label: string;
  value: string;
}): ReactElement {
  return (
    <DescriptionItem label={label}>
      <span className="break-all">{value}</span>
    </DescriptionItem>
  );
}

/**
 * Resolve opaque owner-link refs against the metadata this viewer is already
 * authorized to see. A ref that no longer resolves — unlinked, deleted, or a
 * Preparation source this surface cannot read — contributes only its kind to
 * a count. Nothing is guessed, and no earlier render's name is carried over.
 */
function resolveSourceNames(
  refs: readonly GroundingSourceRef[],
  namesById: ReadonlyMap<string, string>,
): {
  sourceNames: string[];
  unresolvedSources: PlayerAIUnresolvedSources;
} {
  const sourceNames: string[] = [];
  const unresolvedSources: PlayerAIUnresolvedSources = {};
  for (const ref of refs) {
    const name =
      ref.source_type === "conversation_attachment"
        ? namesById.get(ref.source_id)
        : undefined;
    if (name) {
      sourceNames.push(name);
      continue;
    }
    // Preserve the kind so the fallback copy stays accurate about what was
    // used: "an attached file" vs "a Preparation source".
    const kind =
      ref.source_type === "conversation_attachment"
        ? "attachment"
        : "preparationSource";
    unresolvedSources[kind] = (unresolvedSources[kind] ?? 0) + 1;
  }
  return { sourceNames, unresolvedSources };
}

function hasUnresolvedSources(value: PlayerAIUnresolvedSources): boolean {
  return Object.values(value).some((count) => (count ?? 0) > 0);
}

/**
 * Validate the module-owned phase payload before translating it into the
 * design system's data-blind notice shape. Historical plain-text system
 * messages deliberately return undefined and retain their compact treatment.
 */
function workshopPhaseUpdate(value: unknown): SystemNotice | undefined {
  if (typeof value !== "object" || value === null) return undefined;
  const update = value as Record<string, unknown>;
  if (
    typeof update["participant_guidance"] !== "string" ||
    !update["participant_guidance"].trim()
  ) {
    return undefined;
  }
  const details = [update["participant_guidance"]];
  if (
    typeof update["player_ai_guidance"] === "string" &&
    update["player_ai_guidance"].trim()
  ) {
    details.push(update["player_ai_guidance"]);
  }
  return { label: "Workshop update", details };
}

function toRailActivityRow(
  activity: RunActivity,
  namesById: ReadonlyMap<string, string>,
): PlayerAIRunActivityRow {
  const { sourceNames, unresolvedSources } = resolveSourceNames(
    activity.source_refs,
    namesById,
  );
  return {
    id: activity.activity_id,
    kind: activity.kind,
    status: activity.status,
    ...(sourceNames.length > 0 ? { sourceNames } : {}),
    ...(hasUnresolvedSources(unresolvedSources) ? { unresolvedSources } : {}),
    // The two TRUSTED presentation facts, carried through unchanged.
    //
    // The client already narrowed `service_kind` to the closed set and bounded
    // `safe_count`, so there is nothing left to validate here — passing them
    // through is the whole mapping. Both are omitted rather than sent as null
    // when absent, so the row's own defaults ("a connected service", no count)
    // decide, in one place.
    //
    // Only the UI's closed widget-type vocabulary is renderable. Connector
    // labels, resource titles and unknown labels still render generically.
    ...(activityWidgetLabel(activity.kind, activity.safe_label) === null
      ? {}
      : {
          widgetLabel: activityWidgetLabel(activity.kind, activity.safe_label),
        }),
    ...(activity.service_kind === null
      ? {}
      : { service: activity.service_kind }),
    ...(activity.safe_count === null
      ? {}
      : { repeatCount: activity.safe_count }),
  };
}

function toRailMessage(
  message: Message,
  namesById: ReadonlyMap<string, string>,
  groupConversation: boolean,
  thread?: {
    readonly messagesById: ReadonlyMap<string, Message>;
    readonly newMessageKey: string | undefined;
    readonly canReply: boolean;
    readonly exchange: Exchange | undefined;
  },
): PlayerAIChatMessage {
  const text = message.content.text;
  const feedbackEligible =
    message.role === "assistant" &&
    typeof text === "string" &&
    text.trim().length > 0;
  const rawGrounding: unknown = message.content.grounding_sources;
  const grounding: GroundingSourceRef[] = Array.isArray(rawGrounding)
    ? rawGrounding.flatMap((value) => {
        if (typeof value !== "object" || value === null) return [];
        const source = value as Record<string, unknown>;
        return (source.source_type === "conversation_attachment" ||
          source.source_type === "vsm_preparation_source") &&
          typeof source.source_id === "string"
          ? [
              {
                source_type: source.source_type,
                source_id: source.source_id,
              },
            ]
          : [];
      })
    : [];
  const rawAttachments: unknown = message.content.attachments;
  const attachments = Array.isArray(rawAttachments)
    ? rawAttachments.flatMap((value) => {
        if (typeof value !== "object" || value === null) return [];
        const attachment = value as Record<string, unknown>;
        return typeof attachment.attachment_id === "string" &&
          typeof attachment.display_filename === "string"
          ? [
              {
                id: attachment.attachment_id,
                name: attachment.display_filename,
              },
            ]
          : [];
      })
    : [];
  const resolved = resolveSourceNames(grounding, namesById);
  const playerAIMention = message.mentions?.find(
    (mention) => mention.type === "player_ai",
  );
  const firstName = message.author_display_name?.trim().split(/\s+/)[0];
  const phaseUpdate = workshopPhaseUpdate(message.content.phase_update);
  // `content.system_action` is LEGACY and deliberately unmapped. It only ever
  // carried `hoshin.workshop.follow_facilitator`, which navigated into
  // `?mode=session` rather than synchronizing anything — connected Session
  // clients already follow phase moves through the product's own
  // `resource_changed` invalidation — and its label collided with the
  // Present-phase spotlight-follow control. New messages omit the key; a
  // historical message that still holds it renders as plain text with no
  // button and no navigation side effect. Do not reintroduce a mapping here.
  return {
    id: message.id,
    conversationId: message.conversation_id,
    role: message.role,
    ...(typeof text === "string" ? { content: text } : {}),
    ...(phaseUpdate ? { systemNotice: phaseUpdate } : {}),
    ...(attachments.length > 0 ? { attachments } : {}),
    ...(message.role === "user"
      ? {
          contextAttachments: sentWidgetAttachments(
            message.content.interaction_context,
          ),
        }
      : {}),
    ...(groupConversation && message.role === "user"
      ? { senderName: firstName || "Participant" }
      : {}),
    ...(playerAIMention?.occurrence_indices?.length
      ? {
          playerAIMentionOccurrenceIndices: [
            ...playerAIMention.occurrence_indices,
          ],
        }
      : {}),
    ...(feedbackEligible ? { feedbackEligible: true } : {}),
    ...(grounding.length > 0
      ? {
          widget: {
            kind: "documentGrounding" as const,
            sourceNames: resolved.sourceNames,
            ...(hasUnresolvedSources(resolved.unresolvedSources)
              ? { unresolvedSources: resolved.unresolvedSources }
              : {}),
          },
        }
      : {}),
    ...(message.viewer_feedback
      ? {
          viewerFeedback: {
            rating: message.viewer_feedback.rating,
            comment: message.viewer_feedback.comment,
          },
        }
      : {}),
    ...(thread ? threadFields(message, thread) : {}),
    ts: message.created_at,
  };
}

/**
 * What a message row carries for the MVP thread behaviour: the new-message
 * key (Task 1), person-mention highlights (Task 6), its trigger preview,
 * reply reference and Reply action (Task 7), the stopped marker (Task 14) and
 * whose exchange it is (Task 17).
 */
function threadFields(
  message: Message,
  thread: NonNullable<Parameters<typeof toRailMessage>[3]>,
): Partial<PlayerAIChatMessage> {
  const response = isPlayerAIResponse(message);
  const personMentions = personMentionsOf(message);
  const triggerPreview = triggerPreviewOf(message);
  const replyTo = replyToOf(message, thread.messagesById);
  const countAs =
    message.role === "user" ? "human" : response ? "ai" : undefined;
  return {
    sequence: message.sequence,
    ...(thread.newMessageKey ? { newMessageKey: thread.newMessageKey } : {}),
    ...(personMentions ? { personMentions } : {}),
    ...(triggerPreview ? { triggerPreview } : {}),
    ...(replyTo ? { replyTo } : {}),
    ...(thread.canReply && response ? { canReply: true } : {}),
    ...(message.content.stopped === true ? { stopped: true } : {}),
    ...(thread.exchange
      ? {
          exchange: thread.exchange,
          ...(countAs ? { exchangeCountAs: countAs } : {}),
        }
      : {}),
  };
}

function playerAiClientAction(value: unknown): PlayerAIClientAction | null {
  if (typeof value !== "object" || value === null) return null;
  const action = value as Record<string, unknown>;
  if (
    typeof action.action_id !== "string" ||
    !action.action_id ||
    typeof action.contract_version !== "number" ||
    !Number.isInteger(action.contract_version) ||
    action.contract_version < 1 ||
    typeof action.label !== "string" ||
    !action.label.trim()
  ) {
    return null;
  }
  return value as PlayerAIClientAction;
}

/**
 * The bounded form descriptor playbook-be compiled for this proposal.
 *
 * Read defensively and returned as `null` on anything unexpected: the preview
 * is durable JSON, so a row written by an older service legitimately has no
 * descriptor, and a row written by a newer one may carry a shape this bundle
 * does not know. Neither may break the card.
 */
export function proposalForm(proposal: Proposal): EditableProposalForm | null {
  const form = (proposal.preview as Record<string, unknown> | undefined)?.[
    "form"
  ];
  if (typeof form !== "object" || form === null) return null;
  const candidate = form as Record<string, unknown>;
  if (candidate["version"] !== 1) return null;
  if (typeof candidate["revisionHash"] !== "string") return null;
  if (!Array.isArray(candidate["fields"])) return null;
  // An empty descriptor is the compiler's incomplete/oversized preview signal.
  if (candidate["fields"].length === 0) return null;
  if (typeof candidate["values"] !== "object" || candidate["values"] === null) {
    return null;
  }
  // Old saved descriptors also pass through the new structured renderer.
  // Reject incomplete or excessively nested values instead of hiding content.
  const pending = [{ value: candidate["values"], depth: 0 }];
  let nodes = 0;
  while (pending.length > 0) {
    const item = pending.pop()!;
    if (++nodes > 5000 || item.depth > 40) return null;
    if (item.value !== null && typeof item.value === "object") {
      for (const value of Object.values(item.value))
        pending.push({ value, depth: item.depth + 1 });
    }
  }
  return form as EditableProposalForm;
}

/**
 * The bounded, machine-readable reason a batch is discarded to be replaced.
 *
 * Short, fixed, and not a sentence about the user's data. It goes into the
 * durable decision record, which is read by people and by log pipelines; a
 * free-text reason there is a place for a provider's or a model's words to end
 * up in an audit trail.
 */
const UNSUPPORTED_REVIEW_FORM_REASON = "unsupported_review_form";

/**
 * The FIXED replacement request. Not built from the batch it replaces.
 *
 * Deliberately carries no proposal arguments, no provider response, no target
 * and no form payload. The old batch is exactly the thing the browser could not
 * render, so quoting any part of it back would put an unreviewed remote payload
 * into a durable message — and the replacement Run has the conversation and the
 * current context it needs without it.
 */
const REGENERATE_SUGGESTIONS_MESSAGE =
  "@Player AI Those suggestions could not be reviewed. Please suggest them again.";

/**
 * What a regeneration is keyed on.
 *
 * The BATCH when there is one, because a batch is one decision and one
 * replacement; the proposal otherwise. Keying on the proposal for a batched
 * card would let each member's card start its own regeneration.
 */
function regenerationKey(proposal: Proposal): string {
  return proposal.approval_batch_id === null
    ? `proposal:${proposal.id}`
    : `batch:${proposal.approval_batch_id}`;
}

/**
 * Can this group be reviewed at all?
 *
 * An approval batch is ATOMIC, so one member whose descriptor is missing or of
 * a version this bundle does not understand makes the whole batch unreviewable.
 * Partial review is not an option — approving three of five writes is not the
 * decision the batch represents. The Player AI store creates batch membership
 * only through `persist_integration_proposal_batch` from
 * `IntegrationProposalDraft`; native product Proposals never receive an
 * `approval_batch_id`. Keeping this fail-closed also protects a malformed
 * durable row instead of approving an unrendered action.
 */
function hasUnsupportedReviewForm(group: Proposal[]): boolean {
  return group.some((proposal) => proposalForm(proposal) === null);
}

/**
 * PB-5447 — approval cards already announced in this page. Module-wide, not
 * per instance: the rail, the popover and fullscreen are separate hosts, and
 * switching between them or reopening Player AI must not say the same card
 * again. Proposal ids are unique, so cards from other conversations never
 * collide.
 */
const announcedApprovalIds = new Set<string>();

/**
 * A batch is one approval however its members' updates arrive: while one
 * member's frame lands before another's, the waiting rest regroups under a
 * new leader. Remembered by the batch, it is not said again.
 */
function approvalAnnouncementKey(group: Proposal[]): string {
  const batch = group[0]!.approval_batch_id;
  return batch ? `batch:${batch}` : group[0]!.id;
}

/** The reminder's words for one approval: a canvas change set, else its title. */
function approvalNoticeLabels(
  group: Proposal[],
): Pick<PlayerAIApprovalNotice, "label" | "reviewLabel"> {
  if (isCanvasChangeProposal(group[0]!)) {
    return {
      label: "Canvas approval waiting",
      reviewLabel: "Review canvas approval",
    };
  }
  const title = group.length === 1 ? group[0]!.title.trim() : "";
  return title
    ? {
        label: `Approval waiting: ${title}`,
        reviewLabel: `Review approval: ${title}`,
      }
    : { label: "Approval waiting", reviewLabel: "Review approval" };
}

/**
 * The server's own classification of a Proposal as a governed external write
 * (`INTEGRATION_PRECONDITION_KINDS` in Player AI). Mirrored, not derived: the
 * precondition is the only durable fact that tells a governed write from a
 * native product change when the preview carries no `serviceKind`.
 */
const INTEGRATION_PRECONDITION_KINDS: ReadonlySet<string> = new Set([
  "integration_execution",
  "integration_execution_revision",
]);

/**
 * Does approving this Proposal require the bounded editable review form?
 *
 * ONLY a governed integration write does: its arguments are a remote
 * provider's payload, the form is the reviewed rendering of them, and a user
 * must never be asked to approve a write they cannot inspect. A native
 * product Proposal — a VSM map change, a SOLVE canvas bundle, a workshop
 * command — carries a bounded saved preview the product itself authored and
 * no form at all: it is NON-EDITABLE, not unreviewable. Reading its missing
 * form as "unreviewable" left an authorized owner with a pending card that
 * offered neither Approve/Reject nor the integration-only recovery action,
 * while the waiting Run kept the composer closed — nothing to press, until
 * expiry.
 */
function requiresEditableReviewForm(proposal: Proposal): boolean {
  const kind = proposal.precondition["kind"];
  return (
    proposalService(proposal) !== undefined ||
    (typeof kind === "string" && INTEGRATION_PRECONDITION_KINDS.has(kind))
  );
}

/**
 * The recovery state: a governed batch this bundle cannot render for review.
 * Never true for a group with no governed write in it, whatever it lacks.
 */
function isUnreviewableIntegrationBatch(group: Proposal[]): boolean {
  return (
    group.some(requiresEditableReviewForm) && hasUnsupportedReviewForm(group)
  );
}

function reviewableProposalBatch(
  proposals: Proposal[],
  target: Proposal,
): Proposal[] {
  if (target.approval_batch_id === null) return [target];
  const batch = proposals
    .filter(
      (proposal) =>
        proposal.approval_batch_id === target.approval_batch_id &&
        proposal.status === target.status,
    )
    .sort(
      (left, right) =>
        (left.approval_batch_ordinal ?? 0) -
        (right.approval_batch_ordinal ?? 0),
    );
  return batch.length > 0 ? batch : [target];
}

/**
 * One approval batch is one user decision, so it occupies one timeline card.
 * Status remains part of the key: a historical terminal batch and a refreshed
 * pending batch must never be collapsed into the same piece of UI.
 */
function proposalTimelineGroups(proposals: Proposal[]): Proposal[][] {
  const groups = new Map<string, Proposal[]>();
  for (const proposal of proposals) {
    const key =
      proposal.approval_batch_id === null
        ? `proposal:${proposal.id}`
        : `batch:${proposal.approval_batch_id}:${proposal.status}`;
    const group = groups.get(key);
    if (group) group.push(proposal);
    else groups.set(key, [proposal]);
  }
  return [...groups.values()].map((group) =>
    [...group].sort(
      (left, right) =>
        (left.approval_batch_ordinal ?? 0) -
        (right.approval_batch_ordinal ?? 0),
    ),
  );
}

/** Name a common provider, or use a neutral label for mixed-service batches. */
function reviewServiceLabel(proposals: Proposal[]): string {
  const services = new Set(
    proposals.map((proposal) => proposalService(proposal)),
  );
  if (services.size === 1 && services.has("jira")) return "Jira";
  if (services.size === 1 && services.has("confluence")) return "Confluence";
  return "connected service";
}

function reviewItemsLabel(proposals: Proposal[]): string {
  const service = reviewServiceLabel(proposals);
  if (service === "Jira") return "Affected issues";
  if (service === "Confluence") return "Affected pages";
  return "Affected actions";
}

function proposalMatchesSearch(proposal: Proposal, search: string): boolean {
  const query = search.trim().toLocaleLowerCase();
  if (!query) return true;
  return [proposalTargetLabel(proposal), proposal.title, proposal.summary].some(
    (value) => value?.toLocaleLowerCase().includes(query) === true,
  );
}

function proposalFormHasErrors(
  form: EditableProposalForm,
  values: Record<string, unknown>,
): boolean {
  return form.fields.some((field) => fieldError(field, values));
}

function changedFieldCount(
  form: EditableProposalForm,
  values: Record<string, unknown>,
): number {
  return form.fields.filter(
    (field) =>
      JSON.stringify(values[field.path]) !==
      JSON.stringify(form.values[field.path]),
  ).length;
}

/**
 * The trusted service classification carried on the prepared preview.
 *
 * Narrowed against the closed set here rather than passed through: the value
 * chooses user-visible copy, and a string the design system has no label for
 * must degrade to generic rather than render.
 */
export function proposalService(
  proposal: Proposal,
): PlayerAIIntegrationService | null | undefined {
  const preview = proposal.preview as Record<string, unknown> | undefined;
  if (preview === undefined || !("serviceKind" in preview)) return undefined;
  const service = preview["serviceKind"];
  return service === "jira" || service === "confluence" ? service : null;
}

function toRailProposal(
  proposal: Proposal,
  canApprove: boolean,
  canReject: boolean,
  hasReplacement: boolean,
  proposalGroup: Proposal[] = [proposal],
  /**
   * A regeneration whose replacement request failed after the batch was already
   * rejected. The card keeps its one action so the user has a way back — the
   * old batch is safely discarded either way, and pressing it again only
   * re-asks.
   */
  retryRegeneration = false,
  /**
   * PB-4923 AC6 — the active module's name/params lookups, so a skip notice
   * names the person and the field instead of saying "Someone else is editing
   * text". Optional: absent (or unmatched scope) the notice still renders, with
   * its honest fallbacks.
   */
  widgetLookups?: {
    readonly nameForActor: (userId: string) => string | null;
    readonly widgetParams: (widgetId: string) => unknown;
    readonly revealWidget: (
      widgetId: string,
    ) => PlayerAIProposalCanvasRevealOutcome;
  },
  apiConfig?: ApiClientConfig,
): PlayerAIChatMessage {
  const isBatch = proposalGroup.length > 1;
  const serviceLabel = reviewServiceLabel(proposalGroup);
  const description = isBatch
    ? `These ${proposalGroup.length} actions will be reviewed and decided together.`
    : proposal.status === "stale"
      ? `${proposal.summary} ${
          hasReplacement
            ? "A refreshed Proposal follows."
            : "Player AI is re-reading the current widget state before proposing again."
        }`
      : proposal.summary;
  const service = proposalService(proposal);
  const form = proposalForm(proposal);
  const requiresBatchDecision = proposal.approval_batch_id !== null;
  // REGENERATE-ONLY, and only for the person who may decide. Everyone else in a
  // shared conversation still sees the card and its state; offering them an
  // action the server would refuse is worse than offering none.
  //
  // Reached in two situations, and they are the same state to the user: a
  // pending group nobody can review, and a group whose replacement request
  // failed after its members were already discarded. Scoped to governed
  // integration writes: a native product Proposal never requires the form,
  // so its ordinary Approve/Reject stay available (see
  // `requiresEditableReviewForm`).
  const unsupportedReviewForm =
    canApprove &&
    proposalGroup.some(requiresEditableReviewForm) &&
    ((proposal.status === "pending" &&
      hasUnsupportedReviewForm(proposalGroup)) ||
      retryRegeneration);
  const canvasDelivery = apiConfig
    ? proposalCanvasDeliveryBinding(proposal, canApprove, apiConfig)
    : undefined;
  return {
    id: `proposal-${proposal.id}`,
    role: "assistant",
    ts: proposal.created_at,
    widget: {
      kind: "toolProposal",
      proposalId: proposal.id,
      toolId: `${proposal.tool_id}@${proposal.contract_version}`,
      title: isBatch
        ? `Run ${proposalGroup.length} ${serviceLabel} actions`
        : proposal.title,
      description,
      status: proposal.status,
      // A batched write can only be decided through the grouped review dialog.
      // Hiding per-card decisions prevents an action that the server must
      // reject and avoids implying that one item can be applied independently.
      canApprove: canApprove && !requiresBatchDecision,
      canReject: canReject && !requiresBatchDecision,
      expiresAt: proposal.expires_at,
      input: proposal.input,
      preview: proposalCanvasPreview(proposal),
      executionResult: proposal.result,
      ...(canvasDelivery ? { canvasDelivery } : {}),
      /* PB-4923 AC6 — the lookups the outcome/refusal notices use to name a
         person and a field. `widgetParams` reads the module's already-loaded
         widget cache and never fetches, so a widget this reader has not opened
         simply answers undefined and the field is named by its own key.

         BOTH forms are passed. `appliedParams` serves a single-command result,
         whose widget the proposal names once. `paramsForWidget` serves a
         BUNDLE, whose changes each name their own widget — resolving those from
         one widget's content would label the second widget's field with the
         first's, which is confidently wrong. */
      ...(widgetLookups !== undefined
        ? {
            nameForActor: widgetLookups.nameForActor,
            paramsForWidget: widgetLookups.widgetParams,
            appliedParams: proposalWidgetId(proposal)
              ? widgetLookups.widgetParams(proposalWidgetId(proposal) as string)
              : undefined,
            /* Task 9 — where this proposal's applied changes landed, and how to
               go and look at one. Both are omitted for anything not applied, so
               a pending or failed card cannot imply a result exists. */
            canvasDestinations: proposalCanvasDestinations(proposal),
            onViewOnCanvas: widgetLookups.revealWidget,
          }
        : {}),
      /* PB-4923 AC6 — a conflict refusal is a sentence, not a blob.
         `WIDGET_EDITING_IN_PROGRESS` carries which field and who in its
         `details`, and stringifying the error object put
         `{"code":…,"details":{…}}` on screen. `proposalApplyErrorCopy` returns
         null for anything it does not recognise, so every other error renders
         exactly as before.

         It is carried as `conflictNotice` as well as `error` because the canvas
         card ignores `error` by design — see the field's docstring. */
      ...(() => {
        if (Object.keys(proposal.error).length === 0) return { error: null };
        /*
         * PB-5276 — ONE description per refused apply, and the copy block that
         * goes with it.
         *
         * The description is built from the error's CODE only; the `details`
         * object is never read into copy, so nothing a connector or a product
         * backend puts there can reach the card. The diagnostics carry the
         * Proposal and Run ids and the Proposal's own status, which is what a
         * support reader needs to find the apply — they are allowlisted by
         * `describePlayerAIFailure`, so an id-shaped field is kept and anything
         * else is dropped.
         */
        const described = describePlayerAIFailure({
          source: "proposal_apply",
          code: (proposal.error as { code?: unknown }).code,
          proposalId: proposal.id,
          runId: proposal.run_id,
          occurredAt: proposal.updated_at,
          runOutcome: proposal.status,
        });
        const details = playerAIFailureDetails(described);
        const copy = proposalApplyErrorCopy(proposal.error, {
          ...(proposal.status === "failed" || proposal.status === "stale"
            ? { toolId: proposal.tool_id }
            : {}),
          ...(widgetLookups !== undefined
            ? { nameForActor: widgetLookups.nameForActor }
            : {}),
          ...(proposalWidgetId(proposal) && widgetLookups !== undefined
            ? {
                params: widgetLookups.widgetParams(
                  proposalWidgetId(proposal) as string,
                ),
              }
            : {}),
        });
        // The two RECOGNISED refusals keep their attributed sentence — they
        // name which field and who, which no family copy can — and the copy
        // control is offered beside them all the same.
        if (copy !== null) {
          return { error: copy, conflictNotice: copy, errorDetails: details };
        }
        /*
         * Otherwise the catalogue's apply-stage sentence replaces what used to
         * be `JSON.stringify(proposal.error)`.
         *
         * Deliberately NOT carried as `conflictNotice`: that slot is for a
         * DOMAIN conflict the reader can act on, and a generic sentence there
         * would dress an unknown refusal up as a known one.
         */
        return {
          error: `${described.title} ${described.message}`,
          errorDetails: details,
        };
      })(),
      // Presence of `service` is what selects the compact integration card, so
      // it is passed only when the preview actually declared one.
      ...(service !== undefined ? { service } : {}),
      ...(!isBatch && proposalTargetLabel(proposal)
        ? { targetLabel: proposalTargetLabel(proposal) as string }
        : {}),
      ...(isBatch ? { affectedCount: proposalGroup.length } : {}),
      ...(service !== undefined
        ? { riskLabel: "Write · Approval required" }
        : {}),
      // Reviewable when EVERY member has something bounded to review. A batch
      // is one decision, so a single unrenderable member takes the whole group
      // to the recovery state rather than to a dialog that could only show
      // part of what is being approved.
      ...(form !== null &&
      !hasUnsupportedReviewForm(proposalGroup) &&
      !proposalGroup.some((item) => item.status === "expired")
        ? {
            canReview: true,
            ...(isBatch
              ? { reviewLabel: `Review ${proposalGroup.length} suggestions` }
              : {}),
          }
        : {}),
      ...(unsupportedReviewForm ? { unsupportedReviewForm: true } : {}),
    },
  };
}

/**
 * A safe target label for the compact card, from the descriptor's own
 * read-only fields.
 *
 * The descriptor is the only trusted source for this: its labels are
 * connector-authored or humanised keys, and its values passed through the
 * platform's bounds. Reading `proposal.input` directly would put an
 * unsanitised provider value on the card.
 */
/**
 * The widget a proposal's RESULT belongs to, for the params lookup.
 *
 * Read from the result rather than the target ref: a canvas bundle's target is
 * the surface, and each change names its own widget. A bundle therefore has no
 * single widget and answers `null` — its notice names fields by key, which is
 * correct rather than picking one member's params and mislabelling the rest.
 */
function proposalWidgetId(proposal: Proposal): string | null {
  const result: unknown = proposal.result;
  if (typeof result !== "object" || result === null) return null;
  const widgetId = (result as Record<string, unknown>)["widget_id"];
  return typeof widgetId === "string" && widgetId.length > 0 ? widgetId : null;
}

function proposalCanvasDeliveryBinding(
  proposal: Proposal,
  canRepair: boolean,
  config: ApiClientConfig,
): PlayerAICanvasDeliveryBinding | undefined {
  if (proposal.status !== "applied") return undefined;
  const scope = asRecord(proposal.scope_ref);
  const target = asRecord(proposal.target_ref);
  // Background specialist bundles carry a workspace target and an empty
  // scope_ref. Resolve that persisted target as well as scoped widget proposals.
  const solveId =
    scope?.["module_id"] === "solve" &&
    scope["surface_type"] === "solve_canvas" &&
    typeof scope["surface_id"] === "string"
      ? scope["surface_id"]
      : target?.["type"] === "workspace" &&
          target["workspace_type"] === "solve" &&
          typeof target["workspace_id"] === "string"
        ? target["workspace_id"]
        : undefined;
  if (!solveId) return undefined;
  const result = asRecord(proposal.result);
  if (!result) return undefined;
  const changes = asArray(result["changes"]);
  const rows = changes.length > 0 ? changes : [result];
  const commands = rows.flatMap((item) => {
    const change = asRecord(item);
    const commandId = change?.["canvas_command_id"];
    if (typeof commandId !== "string") return [];
    const initial = change?.["canvas_sync_status"];
    return [
      {
        commandId,
        initialState:
          initial === "applied" || initial === "failed" ? initial : "pending",
      },
    ] as const;
  });
  if (commands.length === 0) return undefined;
  const path = (commandId: string) =>
    `/v1/solves/${encodeURIComponent(solveId)}/canvas-deliveries/${encodeURIComponent(commandId)}`;
  return {
    commands,
    canRepair,
    getStatus: (commandId) =>
      apiCall<PlayerAICanvasDeliveryStatus>(config, "GET", path(commandId)),
    retry: (commandId) =>
      apiCall<PlayerAICanvasDeliveryStatus>(
        config,
        "POST",
        `${path(commandId)}/retry`,
      ),
    discard: (commandId) =>
      apiCall<PlayerAICanvasDeliveryStatus>(
        config,
        "POST",
        `${path(commandId)}/discard`,
        { confirm: true },
      ),
  };
}

/** A canvas change set — the card that reads "Canvas changes" and "Approve all". */
function isCanvasChangeProposal(proposal: Proposal): boolean {
  return (
    asRecord(proposal.preview)?.["kind"] === "canvas_bundle" ||
    proposal.tool_id === "canvas.widget_changes.apply"
  );
}

function proposalCanvasPreview(proposal: Proposal): Proposal["preview"] {
  const preview = asRecord(proposal.preview);
  if (preview?.["kind"] !== "canvas_bundle") return proposal.preview;
  const changes = asArray(preview["changes"]);
  const inputs = asArray(asRecord(proposal.input)?.["changes"]);
  // The saved normalized input and preview are emitted together by preflight,
  // after dropping no-ops. Applied results may be reordered or omit changes;
  // never use their array positions to identify a preview row.
  if (changes.length !== inputs.length) return proposal.preview;
  return {
    ...preview,
    changes: changes.map((entry, index) => {
      const change = asRecord(entry);
      if (
        !change ||
        asRecord(change["target_ref"])?.["widget_id"] ||
        change["widget_id"]
      )
        return entry;
      const target = asRecord(asRecord(inputs[index])?.["target_ref"]);
      return typeof target?.["widget_id"] === "string"
        ? { ...change, target_ref: target }
        : entry;
    }),
  };
}

/**
 * Task 9 — the widgets an APPLIED proposal actually landed on, by stable id.
 *
 * Read from the execution RESULT, never the saved preview: the result is what
 * the server did, it names each widget by identity, and for a created widget
 * that identity is the one the apply minted. A title, an array position or a
 * saved coordinate would all be wrong the moment somebody renames, reorders or
 * moves something — which is exactly what this action has to survive.
 *
 * Three exclusions, each of them the same rule: never imply a result exists.
 *
 *  - a proposal that is not `applied` contributes nothing;
 *  - a change whose operations were ALL skipped contributes nothing, because
 *    nothing of it reached the canvas;
 *  - `widget_unplace` contributes nothing — its whole effect is that the widget
 *    is no longer there to visit.
 */
function proposalCanvasDestinations(
  proposal: Proposal,
): readonly PlayerAIProposalCanvasDestination[] {
  if (proposal.status !== "applied") return [];
  const result = asRecord(proposal.result);
  if (result === null) return [];
  const previewChanges = asArray(
    asRecord(proposalCanvasPreview(proposal))?.["changes"],
  );
  const resultChanges = asArray(result["changes"]);
  const rows = resultChanges.length > 0 ? resultChanges : [result];

  const destinations: PlayerAIProposalCanvasDestination[] = [];
  const seen = new Set<string>();
  rows.forEach((row, index) => {
    const change = asRecord(row);
    if (change === null) return;
    const widgetId = change["widget_id"];
    if (typeof widgetId !== "string" || widgetId.length === 0) return;
    // Applied nothing? Then there is nothing of this change to look at.
    if (change["action"] === "unplace") return;
    if (
      change["action"] !== "create" &&
      asArray(change["applied"]).length === 0
    )
      return;
    if (seen.has(widgetId)) return;

    // The saved preview supplies the human label and the phase. It is matched
    // by widget id where it can be, and only falls back to position for the
    // single-change shape where there is exactly one of each.
    const preview =
      matchPreviewChange(previewChanges, widgetId) ??
      (rows.length === 1 && previewChanges.length === 1
        ? asRecord(previewChanges[0])
        : null);
    const previewBody = asRecord(preview?.["preview"]);
    if (previewBody?.["kind"] === "widget_unplace") return;
    const label = preview?.["label"];
    const phase = previewBody?.["phase"];
    seen.add(widgetId);
    destinations.push({
      widgetId,
      label:
        typeof label === "string" && label.trim()
          ? label
          : `Widget ${index + 1}`,
      ...(typeof phase === "string" && phase ? { phase } : {}),
    });
  });
  return destinations;
}

function matchPreviewChange(
  previewChanges: readonly unknown[],
  widgetId: string,
): Record<string, unknown> | null {
  for (const entry of previewChanges) {
    const change = asRecord(entry);
    const target = asRecord(change?.["target_ref"]);
    if (target?.["widget_id"] === widgetId) return change;
  }
  return null;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function asArray(value: unknown): readonly unknown[] {
  return Array.isArray(value) ? value : [];
}

function proposalTargetLabel(proposal: Proposal): string | undefined {
  const form = proposalForm(proposal);
  if (form === null) return undefined;
  const fixed = form.fields.filter((field) => !field.editable);
  const parts = fixed
    .map((field) => form.values[field.path])
    .filter(
      (value): value is string => typeof value === "string" && value !== "",
    );
  return parts.length > 0 ? parts.join(" · ") : undefined;
}

function toRailPlatformActionBatch(
  batch: PlatformActionBatch,
): PlayerAIChatMessage {
  // Explicit tuple type: inferring from the pair literal widens both sides to
  // `string | PlatformActionItemResult`, which loses the status discriminant.
  const resultByActionId = new Map<string, PlatformActionItemResult>(
    (batch.result?.results ?? []).map(
      (item): [string, PlatformActionItemResult] => [item.action_id, item],
    ),
  );
  return {
    id: `platform-action-batch-${batch.id}-${batch.revision}-${batch.status}`,
    role: "assistant",
    ts: batch.updated_at,
    widget: {
      kind: "actionBatch",
      batchId: batch.id,
      revision: batch.revision,
      frozenHash: batch.frozen.frozen_hash,
      status: batch.status,
      actionCount: batch.frozen.action_count,
      canEdit: batch.status === "pending_confirmation",
      canApply: batch.status === "pending_confirmation",
      groups: batch.frozen.groups.map((group) => ({
        owner: group.owner,
        label: group.label,
        actions: group.actions.map((item) => {
          const result = resultByActionId.get(item.action.action_id);
          return {
            actionId: item.action.action_id,
            kind: item.action.kind,
            label: item.preview.label,
            input: item.action.input,
            ...(result
              ? {
                  status: result.status,
                  ...(result.status !== "untouched"
                    ? { message: result.message }
                    : {}),
                }
              : {}),
          };
        }),
      })),
      ...(batch.remainder_batch_id
        ? { remainderBatchId: batch.remainder_batch_id }
        : {}),
      ...(batch.result?.remainder
        ? { remainderActionCount: batch.result.remainder.action_count }
        : {}),
    },
  };
}

function toRailThread(
  conversation: Conversation,
  unavailableReason: string | null,
): PlayerAIThread {
  const updatedAt = Date.parse(conversation.updated_at);
  return {
    id: conversation.id,
    title: conversation.title,
    messages: [],
    scope:
      conversation.conversation_type === "workspace"
        ? String(conversation.workspace_type)
        : "private",
    updatedAt: Number.isNaN(updatedAt) ? Date.now() : updatedAt,
    ...(unavailableReason ? { unavailableReason } : {}),
  };
}
